import {
	type Action,
	type BoundActions,
	createActionsHook,
	createContainer,
	createHook,
	type HookActionsFunction,
	type HookFunction,
	type OverrideContainerComponent,
	type StoreActionApi,
} from 'react-sweet-state';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type {
	FollowUpSource,
	InteractionSource,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	type ExperienceNameType,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { AI_MATE_ATTRIBUTES } from '@atlassian/conversation-assistant-instrumentation/types/analytics-properties-context';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import {
	type Agent,
	type AlphaExtendedThinkingMode,
	type AssistantMessage,
	type FetchStreamReturn,
	isAssistantMessage,
	isChannelNotFoundError,
	isConversationChannelDataMessage,
	isFollowUpMessage,
	isHumanMessage,
	isRateLimitedError,
	isSSEMessage,
	isStreamAnswerPart,
	isStreamAttachmentBlock,
	isStreamAUPError,
	isStreamError,
	isStreamPromptBlock,
	isStreamPromptRedaction,
	isStreamResponseBlock,
	isStreamResponseRedaction,
	isStreamResponse,
	isStreamTrace,
	type MessageFileId,
	type ParsedSSEMessage,
	type RovoLabsConfirmedTools,
	type StreamError,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type MessageActionInvocationStatus,
	type MessageActionPayload,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { type StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	type CsmMediaAttachment,
	isStoreAssistant,
	isStoreError,
	isStoreFollowUp,
	isStoreHuman,
	isStoreTwilioLiveChatMessage,
	type StoreChatItem,
	type StoreGuardBlock,
	type Trace,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { RovoInstrumentedError } from '@atlassian/conversation-assistant-utils/httpUtils';

import adfToText from '../../common/utils/adf-to-text';
import { interactionMetrics } from '../../common/utils/browser-metrics';
import { buildMessageAri } from '../../common/utils/build-message-ari';
import { extractHelpCenterIdFromAri } from '../../common/utils/extract-help-center-id-from-ari';
import { randomIntFromInterval } from '../../common/utils/id-generator';
import type { ChatMode } from '../../extensions/twilio/ocg-client';
import type { OcgClient } from '../../extensions/twilio/use-ocg-client';
import type { CsmChat } from '../../services/assistance-service/types';

import { createChatStore } from './create-chat-store';
import { createStoreAbortController } from './store-abort-controller';
import {
	addTwilioAgentTypingImpl,
	addTwilioLiveChatHumanMessageImpl,
	addTwilioLiveChatMessageImpl,
	type AddTwilioLiveChatMessageParams,
	backfillLiveChatMessagesImpl,
	type BackfillLiveChatMessagesParams,
	checkInitialUnreadMessagesImpl,
	type CheckInitialUnreadMessagesParams,
	cleanupTwilioConversationImpl,
	cleanupTwilioTokenRefreshImpl,
	clearTwilioAgentsTypingImpl,
	clearTwilioConversationsWithMetadataImpl,
	initializeDirectLiveChatImpl,
	hydrateTwilioConversationImpl,
	type HydrateTwilioConversationParams,
	updateTwilioUnreadMessagesCountImpl,
	type UpdateTwilioUnreadMessagesCountParams,
	initializeTwilioConversationImpl,
	isPendingDirectLiveChat,
	type OcgTwilioClientModule,
	removeTwilioAgentTypingImpl,
	setTwilioAgentJoinedImpl,
	type SetTwilioAgentJoinedParams,
	type TwilioAgentTypingParams,
} from './twilio-live-chat';
import type {
	ChatStoreAction,
	ChatStoreContainerProps,
	ChatStoreState,
	OmniEnhancedConversation,
	WidgetChatStorePrivateState,
} from './types';
import {
	addAnswerPart,
	addRequestIdToLatestHumanMessage,
	applyRedactionToLatestAssistantMessage,
	applyRedactionToLatestHumanMessage,
	checkActiveConversationMatchesSource,
	clearStatus,
	containsServiceDeskSource,
	createStoreAssistant,
	createStoreAssistantWithContent,
	createStoreError,
	createStoreHuman,
	createStoreHumanForTwilioLiveChat,
	createStoreHumanWithContent,
	csmMediaAttachmentsFromFileIds,
	generateTmpMessageId,
	genIntId,
	getAgentMessageById,
	getConversation,
	getLastRequestIdFromConversation,
	getLiveChatPluginExtraInfo,
	getMessageHistory,
	getSourceAgentId,
	getTraceFromStreamMessage,
	hasEscalationPluginWithExtraInfo,
	isDirectLiveChat,
	isEmptyConversation,
	mapOmniConversationToStore,
	markStreamingMessagesAsComplete,
	replaceAnswerData,
	setIsAborted,
} from './utils';

export type SystemMessages = {
	liveChatPaused: string;
	liveChatPausedWithIssuePrefix: string;
	withIssueSuffix: string;
	agentUnavailable: string;
	agentUnavailableWithIssuePrefix: string;
};

const buildWithIssueMessage = ({
	prefix,
	suffix,
	issueKey,
	requestUrl,
}: {
	prefix: string;
	suffix: string;
	issueKey: string;
	requestUrl: string;
}): DocNode =>
	({
		version: 1,
		type: 'doc',

		content: [
			{
				type: 'paragraph',
				content: [
					{ type: 'text', text: prefix },
					{
						type: 'text',
						text: issueKey,
						marks: [{ type: 'link', attrs: { href: requestUrl } }],
					},
					{ type: 'text', text: suffix },
				],
			},
		],
	}) as DocNode;

const getAgentUnavailableContent = ({
	issueKey,
	requestUrl,
	systemMessages,
}: {
	issueKey?: string;
	requestUrl?: string;
	systemMessages: SystemMessages;
}) => {
	if (issueKey && requestUrl) {
		return buildWithIssueMessage({
			prefix: systemMessages.agentUnavailableWithIssuePrefix,
			suffix: systemMessages.withIssueSuffix,
			issueKey,
			requestUrl,
		});
	}
	return systemMessages.agentUnavailable;
};

export const dedupeTraceMessagesWithLatest = (traces: Trace[]): Trace[] => {
	const messages = traces.map((trace) => trace.messageContent);
	return traces.filter((trace, i) => messages.lastIndexOf(trace.messageContent) === i);
};

export class StreamErrorException extends Error {
	detail: Pick<StreamError, 'message' | 'metadata'>;
	exceptionName?: string;
	constructor(
		message: string,
		detail: Pick<StreamError, 'message' | 'metadata'>,
		exceptionName?: string,
	) {
		super(message);
		this.name = this.constructor.name;
		if (typeof Error.captureStackTrace === 'function') {
			Error.captureStackTrace(this, this.constructor);
		}
		this.detail = detail;
		this.exceptionName = exceptionName;
	}
}

export class NoFinalResponseError extends Error implements RovoInstrumentedError {
	errorAttributes: RovoInstrumentedError['errorAttributes'];

	constructor(
		message: string,
		params: {
			traceId: string | undefined;
		},
	) {
		super(message);
		this.name = this.constructor.name;
		this.errorAttributes = {
			traceId:
				params.traceId ??
				`NoFinalResponse-${Date.now().toString(16)}-${Math.floor(Math.random() * 1e4).toString(
					16,
				)}`,
		};
	}
}

const getConversationMessages = (state: ChatStorePrivateState, conversationId: string) =>
	state.conversations[conversationId]?.messages ?? [];

/**
 * Returns true when the last item in `messages` is a `StoreError` that is
 * semantically equivalent to `candidate` — i.e. would be a duplicate if
 * appended.
 *
 * Two `StoreError`s are considered semantically equivalent when their
 * user-visible content and identifying error attributes match. Volatile
 * fields that always differ between two distinct error events (`id`,
 * `time_created`) are intentionally excluded from the comparison.
 *
 * Used by the generic `createSafeAction` catch handler to avoid re-appending
 * the same error every time the user revisits a conversation whose
 * underlying resource is permanently broken (e.g. a deleted Twilio
 * conversation, a 404 from `assistanceService.getChat()`).
 */
const isDuplicateOfLastStoreError = (messages: StoreChatItem[], candidate: StoreError): boolean => {
	const last = messages[messages.length - 1];
	if (!last || !isStoreError(last)) {
		return false;
	}
	return (
		last.content === candidate.content &&
		last.status_code === candidate.status_code &&
		last.message_template === candidate.message_template &&
		last.errorName === candidate.errorName
	);
};

const getConversationTraces = (state: ChatStorePrivateState, conversationId: string) =>
	state.conversations[conversationId]?.traces ?? [];

/**
 * Util action to initialize conversation with conversationId
 */
const initializeConversationData =
	({
		conversationId,
		data: conversationData,
	}: {
		conversationId: string;
		data: Conversation;
	}): ChatStoreAction =>
	({ getState, setState }) => {
		setState({
			conversations: {
				...getState().conversations,
				[conversationId]: {
					...(getState().conversations[conversationId] ?? {}),
					...conversationData,
				},
			},
		});
	};

/**
 * Util action to allow updating the conversation based on `conversationId`
 * without needing to keep spreading the existing
 *
 * Similar to `initializeConversationData`, the difference is, it will not allow the data to be updated if there is no existing conversation
 */
const setConversationData =
	({
		conversationId,
		data: conversationData,
	}: {
		conversationId: string;
		data: Partial<Conversation>;
	}): ChatStoreAction =>
	({ getState, setState }) => {
		if (!getState().conversations[conversationId]) {
			return;
		}

		setState({
			conversations: {
				...getState().conversations,
				[conversationId]: {
					...(getState().conversations[conversationId] ?? {}),
					...conversationData,
				},
			},
		});
	};

/**
 * Action Factory
 * Adds custom _middlewares_ to handle common cases and unexpected errors.
 * This is a hack to attach common logic to all actions.
 * It should not use the store action dispatcher. Instead, should call the actionCallback directly.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any;

export const createSafeAction =
	<T extends AnyFunction, R = void>(
		actionHOF: T,
		props?: {
			experienceName?: ExperienceNameType;
		},
	) =>
	(
		...args: Parameters<T>
	): Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<R>> =>
	async (
		api: StoreActionApi<WidgetChatStorePrivateState>,
		containerProps: ChatStoreContainerProps,
	) => {
		/*
		Add custom logic here
		*/
		// General try/catch statement to handle unexpected errors
		try {
			// Here we call the action callback
			// Same as dispatch(actionHOF)
			const action = actionHOF(...args);
			// Here we execute the action i.e. what happens internally when the action is dispatched
			const result = await action(api, containerProps);
			return result as R;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			const storeError = createStoreError(error);
			const isRateLimited = storeError.status_code === 429 || isRateLimitedError(error);

			if (props?.experienceName && !isRateLimited) {
				containerProps.experienceTracker.fail({
					name: props.experienceName,
					error,
				});
			}

			const { setState, getState, dispatch } = api;
			const currentConversationId = getState().currentConversationId;

			// Trigger the onError callback for every error that hasn't been handled within the action itself
			// TODO: revisit this with a more proper error handler. [CSMAI-452](https://product-fabric.atlassian.net/browse/CSMAI-452)
			containerProps.setGlobalError?.(new Error(storeError.content));

			// For now we will log all the message into currentConversationId,
			// once we implement multiple conversation,
			// the error should be redirected to correct conversation id
			// Update: 2024-08-13 - We always have an empty conversation so we
			// check if current is empty for now which is effectively the same
			// as the fresh start that existed previously TODO: Clean up
			if (
				!getState().conversations[currentConversationId] ||
				isEmptyConversation(currentConversationId)
			) {
				containerProps.setConversationError({
					type: 'conversationEmptyOrNotPresentInStore',
					underlyingError: storeError,
				});
				return undefined as R;
			}

			if (isRateLimited) {
				setState({
					conversations: {
						...getState().conversations,
						[currentConversationId]: {
							...getState().conversations[currentConversationId],
							...clearStatus(),
						},
					},
				});

				containerProps.setConversationError({
					type: 'rateLimited',
					underlyingError: storeError,
					rateLimitedActionType: 'generic',
				});
				return undefined as R;
			} else {
				// here we should log the error
				// Show the error as a new chat message — but only if the previous
				// message in the conversation isn't already an equivalent error.
				//
				// Without this de-dup guard, revisiting a conversation whose
				// underlying resource is permanently broken (e.g. a deleted Twilio
				// conversation, a 404 on assistanceService.getChat()) causes the
				// same error message to be appended on every visit, stacking up
				// indefinitely as the user navigates back to it from chat history.
				const existingMessages = getConversationMessages(getState(), currentConversationId);
				const isDuplicate = isDuplicateOfLastStoreError(existingMessages, storeError);
				// In both branches we clear the loading/streaming status so the
				// UI doesn't look stuck. When the new error is a duplicate of
				// the last one, we deliberately omit the `messages` key so the
				// existing array reference is preserved (avoiding a needless
				// re-allocation) and the duplicate isn't appended. Otherwise
				// we append the new error to the message list.
				dispatch(
					setConversationData({
						conversationId: currentConversationId,
						data: {
							...clearStatus(),
							...(isDuplicate ? {} : { messages: [...existingMessages, storeError] }),
						},
					}),
				);
				return undefined as R;
			}
		}
	};

/**
 * ************************************************************
 * Private Actions
 * ************************************************************
 */

const addStoreChatItem = createSafeAction(
	({
		message,
		conversationId: conversationIdParam,
	}: {
		message: StoreChatItem;
		conversationId?: string;
	}): ChatStoreAction =>
		({ getState, dispatch }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			dispatch(
				setConversationData({
					conversationId,
					data: {
						messages: [...getState().conversations[conversationId].messages, message],
					},
				}),
			);
		},
);

const addHumanMessage = createSafeAction(
	({
		id,
		content,
		conversationId: conversationIdParam,
		isFromSeededConversation = false,
		requestId,
		source,
		fileUploads,
		csmMediaAttachments,
	}: {
		id: string;
		content: DocNode;
		conversationId?: string;
		isFromSeededConversation?: boolean;
		requestId?: string;
		source?: 'TWILIO_LIVE_CHAT';
		fileUploads?: MessageFileId[];
		csmMediaAttachments?: CsmMediaAttachment[];
	}): ChatStoreAction =>
		({ getState, dispatch }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			const message =
				source === 'TWILIO_LIVE_CHAT'
					? createStoreHumanForTwilioLiveChat(id, content, isFromSeededConversation, requestId)
					: createStoreHuman({
							id,
							content,
							isFromSeededConversation,
							requestId,
							fileUploads,
							csmMediaAttachments,
						});
			dispatch(
				addStoreChatItem({
					message,
					conversationId,
				}),
			);
		},
);

const addAssistantMessage = createSafeAction(
	({
		id,
		content,
		conversationId: conversationIdParam,
		agentNamedId,
	}: {
		id: string;
		content: string | DocNode;
		conversationId?: string;
		agentNamedId?: string;
	}): ChatStoreAction =>
		({ getState, dispatch }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			dispatch(
				addStoreChatItem({
					message: createStoreAssistant({ id, content, agentNamedId }),
					conversationId,
				}),
			);
		},
);

export type LiveChatMessage = {
	id: string;
	content: string;
	author?: string;
	timestamp?: string;
	eventType:
		| 'message'
		| 'participant_joined'
		| 'participant_left'
		| 'chat_started'
		| 'system_message'
		| 'chat_closed'
		| 'subscription_error'
		| 'send_message_error';
	liveChatConversationId: string;
	conversationId?: string;
};

const addLiveChatMessage = createSafeAction(
	({
		id,
		content,
		author,
		timestamp,
		eventType,
		liveChatConversationId,
		conversationId: conversationIdParam,
	}: LiveChatMessage): ChatStoreAction =>
		({ getState, dispatch }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const existingMessages = getState().conversations[conversationId]?.messages ?? [];
			const isDuplicate = existingMessages.some((m): boolean => {
				if ('id' in m && (m as { id: unknown }).id === id) {
					return true;
				}
				if (isStoreHuman(m) && typeof m.content === 'object' && m.content?.id === id) {
					return true;
				}
				return false;
			});
			if (isDuplicate) {
				return;
			}

			dispatch(
				addStoreChatItem({
					message: {
						type: 'LIVE_CHAT',
						id,
						content,
						author,
						timestamp,
						eventType,
						liveChatConversationId,
					},
					conversationId,
				}),
			);
		},
);

const addTwilioLiveChatMessage = createSafeAction(
	(params: AddTwilioLiveChatMessageParams): ChatStoreAction => addTwilioLiveChatMessageImpl(params),
);

const addTwilioLiveChatHumanMessage = createSafeAction(
	(params: AddTwilioLiveChatMessageParams): ChatStoreAction =>
		addTwilioLiveChatHumanMessageImpl(params),
);

const backfillLiveChatMessages = createSafeAction(
	(params: BackfillLiveChatMessagesParams): ChatStoreAction => backfillLiveChatMessagesImpl(params),
);

const addTwilioAgentTyping = createSafeAction(
	(params: TwilioAgentTypingParams): ChatStoreAction => addTwilioAgentTypingImpl(params),
);

const removeTwilioAgentTyping = createSafeAction(
	(params: TwilioAgentTypingParams): ChatStoreAction => removeTwilioAgentTypingImpl(params),
);

const clearTwilioAgentsTyping = createSafeAction(
	(): ChatStoreAction => clearTwilioAgentsTypingImpl(),
);

const setTwilioAgentJoined = createSafeAction(
	(params: SetTwilioAgentJoinedParams): ChatStoreAction => setTwilioAgentJoinedImpl(params),
);

const hydrateTwilioConversation = createSafeAction(
	(params: HydrateTwilioConversationParams): ChatStoreAction =>
		hydrateTwilioConversationImpl(params),
);

const updateTwilioUnreadMessagesCount = createSafeAction(
	(params: UpdateTwilioUnreadMessagesCountParams): ChatStoreAction =>
		updateTwilioUnreadMessagesCountImpl(params),
);

const checkInitialUnreadMessages = createSafeAction(
	(params: CheckInitialUnreadMessagesParams): ChatStoreAction =>
		checkInitialUnreadMessagesImpl(params),
);

/**
 * ************************************************************
 * Public Actions
 * ************************************************************
 */

const clearFollowUpMessages = createSafeAction(
	({ conversationId: conversationIdParam }: { conversationId?: string }): ChatStoreAction =>
		({ getState, dispatch }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const { messages } = currentConversation;
			const filteredMessages = messages.filter((message) => !isStoreFollowUp(message));

			dispatch(
				setConversationData({
					conversationId,
					data: { messages: filteredMessages },
				}),
			);
		},
);

type CSMExceptionErrorStreamMessage = {
	type: 'EXCEPTION_ERROR';
	message: string;
	name?: string;
};

const isCSMExceptionError = (error: unknown): error is CSMExceptionErrorStreamMessage => {
	return (
		typeof error === 'object' &&
		error !== null &&
		'type' in error &&
		error.type === 'EXCEPTION_ERROR'
	);
};

export type MessageSource = {
	interactionSource: InteractionSource;
	followUpSource: FollowUpSource;
};

type SendMessageAgentParam = {
	// Optional so that HUMAN_ONLY live-chat callers can invoke `sendMessage`
	// without an AI agent. The action will early-return (with a Sentry log)
	// if `agent` is missing for an AI message, see below.
	agent?: Pick<Agent, 'id' | 'named_id'>;
};

const sendMessage = createSafeAction(
	({
		prompt,
		agent,
		conversationId: conversationIdParam,
		storeUserMessages,
		messageSources,
		sendEditorContext = true,
		isClicked,
		liveChatMessageOnly = false,
		liveChatOptimisticMessageUuid,
		csmMediaAttachments,
		fileUploads,
	}: {
		prompt: DocNode;
		conversationId?: string;
		storeUserMessages: boolean;
		messageSources?: Partial<MessageSource>;
		sendEditorContext?: boolean;
		isClicked?: boolean;
		liveChatMessageOnly?: boolean;
		liveChatOptimisticMessageUuid?: string;
		csmMediaAttachments?: CsmMediaAttachment[];
		/**
		 * Files already uploaded via `assistanceService.uploadFiles` for the
		 * Rovo-for-Service attachment path (`rovo_jsm_create_request_attachment`).
		 * Their ids go to the backend as the top-level `fileIds` array, and the
		 * whole list is kept on the optimistic human message so the sent bubble
		 * can render the attachments immediately.
		 */
		fileUploads?: MessageFileId[];
	} & SendMessageAgentParam): ChatStoreAction =>
		async (
			{ dispatch, getState, setState },
			{
				assistanceService,
				experienceTracker,
				ufoExperiences,
				sendAnalyticsEvent,
				chatContextStoreActions,
				conversationContextStoreActions,
				trackAIMAUEvent,
				eventHandlers,
				ocgClient,
			},
		) => {
			const agentId = agent?.id;
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			const experienceId = uuidv4();

			const ufoExperience = ufoExperiences?.['SEND_MESSAGE']?.getInstance(experienceId);

			const sendMessageFn = async ({ conversationId }: { conversationId: string }) => {
				const currentConversation = getConversation(getState(), conversationId);

				if (currentConversation?.storeAbortController) {
					// Silently aborting if there is another operation in progress
					return;
				}

				const promptId = genIntId();
				const answerId = genIntId();
				const messageSentTimeStamp = new Date().toISOString();
				let generator:
					| AsyncGenerator<
							ParsedSSEMessage<StreamMessage> | StreamMessage | CSMExceptionErrorStreamMessage
					  >
					| undefined;

				const trackingAttributes: {
					pluginsInvoked: Set<string>;
					hasSources: boolean;
					messageId: string;
					humanMessageId?: string;
					requestId?: string;
					sources?: string[];
					usesJsm?: boolean;
					messageActions?: string[];
				} = {
					pluginsInvoked: new Set(),
					hasSources: false,
					messageId: '',
					humanMessageId: '',
					requestId: undefined,
					sources: undefined,
					usesJsm: undefined,
				};

				const initiateAIMAUEvent = () => {
					trackAIMAUEvent({
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						messageId: trackingAttributes.requestId,
						[AI_MATE_ATTRIBUTES.conversationChannelId]: conversationId,
					});
				};

				const isNewConversation = isEmptyConversation(conversationId);

				if (isNewConversation) {
					// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- crypto.randomUUID is unavailable in non-secure HTTP contexts
					conversationId = uuidv4();
				}

				const message = adfToText(prompt);

				eventHandlers.chatMessage.before({
					conversationId,
					message,
				});

				// Handle early creation of conversation data when starting from an empty state
				if (isNewConversation) {
					dispatch(
						createConversationOptimistically({
							id: conversationId,
							status: CHAT_STORE_STATUS.awaitingResponse,
							sourceAgentId: currentConversation?.sourceAgentId ?? agentId,
						}),
					);
				}

				interactionMetrics.sendMessage.start();
				interactionMetrics.timeToFirstAnswerPart.start();

				eventHandlers.chatMessage.started({
					conversationId,
					message,
				});

				const stopInteractionMetricsTimeToFirstAnswerPart = (() => {
					let called = false;
					return (result: 'cancel' | 'stop') => {
						if (!called) {
							if (result === 'stop') {
								interactionMetrics.timeToFirstAnswerPart.stop();
							} else {
								interactionMetrics.timeToFirstAnswerPart.cancel();
							}
							called = true;
						}
					};
				})();

				experienceTracker.start({
					name: ExperienceName.SEND_MESSAGE,
					id: conversationId,
					attributes: {
						conversationId,
						featureArea:
							chatContextStoreActions.getActiveContext() === 'staging-area'
								? 'aifc-staging-area'
								: 'unknown',
					},
				});

				ufoExperience?.start();

				const { abortController, storeAbortController, confirmAbort } =
					createStoreAbortController();

				const updateCurrentConversation = (data: Partial<Conversation>) => {
					dispatch(
						setConversationData({
							conversationId,
							data,
						}),
					);
				};

				updateCurrentConversation({
					status: CHAT_STORE_STATUS.awaitingResponse,
					storeAbortController,
				});
				const liveChatOptimisticId =
					liveChatMessageOnly && liveChatOptimisticMessageUuid
						? (buildMessageAri(getState().liveChatConversationAri, liveChatOptimisticMessageUuid) ??
							promptId)
						: promptId;
				dispatch(
					addHumanMessage({
						id: liveChatOptimisticId,
						content: prompt,
						conversationId,
						...(liveChatMessageOnly ? { source: 'TWILIO_LIVE_CHAT' } : {}),
						...(csmMediaAttachments &&
						csmMediaAttachments.length > 0 &&
						fg('csm_berry_2728_image_attachments_poc')
							? { csmMediaAttachments }
							: {}),
						...(fileUploads && fileUploads.length > 0 && fg('rovo_jsm_create_request_attachment')
							? { fileUploads }
							: {}),
					}),
				);

				// If this is just for adding user message in live chat, return early
				// Don't add assistant message or trigger streaming response
				if (liveChatMessageOnly) {
					updateCurrentConversation({
						status: CHAT_STORE_STATUS.idle,
						storeAbortController: undefined,
					});
					return;
				}

				if (!agent) {
					logException({
						exception: new Error(
							'sendMessage invoked without AI agent for non-liveChatMessageOnly call',
						),
						name: 'SendMessageDroppedNoAiAgent',
					});

					updateCurrentConversation({
						status: CHAT_STORE_STATUS.idle,
						storeAbortController: undefined,
					});
					return;
				}

				dispatch(
					addAssistantMessage({
						id: answerId,
						content: '',
						conversationId,
						agentNamedId: agent.named_id,
					}),
				);
				dispatch(clearFollowUpMessages({ conversationId }));

				// Assumed the insert is successful and inserts at the last position
				const answerIndex = getState().conversations[conversationId].messages.length - 1;

				// CSM media attachments are associated with the message via the
				// top-level `file_ids` array; the wire `content` is already
				// text-only (attachment metadata lives on the optimistic human
				// message via `addHumanMessage` above, not in the ADF).
				const fileIds = csmMediaAttachments?.map((attachment) => attachment.id);
				const hasFileIds = !!fileIds && fileIds.length > 0;

				// Create message stream
				try {
					const result = await assistanceService.sendMessageStream({
						message: prompt,
						agentNamedId: agent.named_id,
						agentId: agentId,
						controller: abortController,
						conversationId,
						storeMessage: storeUserMessages,
						citationsEnabled: true,
						// Two independently-gated attachment paths converge on the same
						// top-level `fileIds` wire field. They are mutually exclusive at
						// runtime (different composers render per experience): `fileIds`
						// is the csm_berry POC path; `fileUploads` is the
						// rovo_jsm_create_request_attachment path.
						...(hasFileIds
							? { fileIds }
							: fileUploads && fileUploads.length > 0 && fg('rovo_jsm_create_request_attachment')
								? { fileIds: fileUploads.map((file) => file.id) }
								: {}),
						browserContext: chatContextStoreActions.getBrowserContext(),
						editorContext: sendEditorContext
							? chatContextStoreActions.getEditorContext()
							: undefined,
						whiteboardContext: sendEditorContext
							? chatContextStoreActions.getWhiteboardContext()
							: undefined,
						additionalContext: {
							csm: conversationContextStoreActions.getConversationContext(),
						},
						optInSSEStream: true,
					});
					generator = result.generator;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					// Returns bad request error because agent is deactivated
					if (
						error instanceof ApiError &&
						error.errorAttributes.errorCode?.includes(
							ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED,
						)
					) {
						experienceTracker.fail({
							name: ExperienceName.SEND_MESSAGE,
							error,
						});

						ufoExperience?.failure();

						initiateAIMAUEvent();
						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							[AI_MATE_ATTRIBUTES.conversationChannelId]: conversationId,
						});

						updateCurrentConversation({
							status: CHAT_STORE_STATUS.deactivatedAgentError,
							traces: [],
							messages: getConversationMessages(getState(), conversationId),
						});

						eventHandlers.chatMessage.failed({
							conversationId,
							message: adfToText(prompt),
							error,
						});

						interactionMetrics.sendMessage.cancel();
						stopInteractionMetricsTimeToFirstAnswerPart('cancel');
						return;
					} else if (error instanceof Error && error.name === 'AbortError') {
						experienceTracker.abort({
							name: ExperienceName.SEND_MESSAGE,
							reason: 'Aborted',
						});

						ufoExperience?.abort();

						initiateAIMAUEvent();
						trackAIMAUEvent({
							type: 'dismissed',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							[AI_MATE_ATTRIBUTES.conversationChannelId]: conversationId,
						});

						updateCurrentConversation(clearStatus());
						eventHandlers.chatMessage.aborted({
							conversationId,
							message: adfToText(prompt),
						});

						interactionMetrics.sendMessage.cancel();
						stopInteractionMetricsTimeToFirstAnswerPart('cancel');

						confirmAbort();

						return;
					} else {
						experienceTracker.fail({
							name: ExperienceName.SEND_MESSAGE,
							error,
						});

						ufoExperience?.failure();

						initiateAIMAUEvent();
						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							[AI_MATE_ATTRIBUTES.conversationChannelId]: conversationId,
						});

						eventHandlers.chatMessage.failed({
							conversationId,
							message: adfToText(prompt),
							error: error instanceof Error ? error : new Error('Unknown error'),
						});

						interactionMetrics.sendMessage.cancel();
						stopInteractionMetricsTimeToFirstAnswerPart('cancel');

						throw error;
					}
				}

				updateCurrentConversation({
					status: CHAT_STORE_STATUS.typingResponse,
					// TODO: This is not localized in the BE yet
					traces: [{ messageContent: 'Analyzing: Reviewing your question.' }],
				});

				/*****************************
				 *
				 * Stream processing START
				 *
				 *****************************/
				// Set to true when a guard event (PROMPT_REDACTION or PROMPT_BLOCKED) is received.
				// The backend always follows these events with an ERROR event to terminate the stream,
				// which should be treated as a successful end of the guard flow, not a real error.
				let guardEventReceived = false;

				for await (const streamMessageObj of generator) {
					const streamMessage = isSSEMessage(streamMessageObj)
						? streamMessageObj.data
						: streamMessageObj;
					//TODO: Investigate if this even runs
					// https://product-fabric.atlassian.net/browse/AIM-3173
					if (abortController.signal.aborted) {
						updateCurrentConversation({
							traces: [{ messageContent: 'Aborted!' }],
							messages: setIsAborted(
								getState().conversations[conversationId]?.messages ?? [],
								answerId,
								true,
								answerIndex,
							),
						});

						confirmAbort();

						eventHandlers.chatMessage.aborted({
							conversationId,
							message: adfToText(prompt),
						});

						experienceTracker.abort({
							name: ExperienceName.SEND_MESSAGE,
							reason: 'Aborted',
						});

						ufoExperience?.abort();

						return;
					}

					// Usually the first stream part
					if (isConversationChannelDataMessage(streamMessage)) {
						ufoExperience?.mark('channelDataMessage');

						updateCurrentConversation({
							name: streamMessage.name,
							lastMessageTimestamp:
								streamMessage.last_message_timestamp ?? new Date().toISOString(),
							timeCreated: streamMessage.time_created,
						});
					}

					// Usually the second stream part, before anything else occurs
					if (isStreamTrace(streamMessage)) {
						ufoExperience?.mark('traces');
						// This is the first moment we have the network request id for E2E event tracking
						// Store requestId on human message for E2E event tracking
						if (!trackingAttributes.requestId) {
							trackingAttributes.requestId = streamMessage.metadata?.request_id;
						}

						updateCurrentConversation({
							messages: addRequestIdToLatestHumanMessage(
								getConversationMessages(getState(), conversationId),
								trackingAttributes.requestId,
							),
						});

						updateCurrentConversation({
							traces: dedupeTraceMessagesWithLatest([
								...getConversationTraces(getState(), conversationId),
								getTraceFromStreamMessage(streamMessage),
							]),
						});
					}

					if (isStreamAnswerPart(streamMessage)) {
						ufoExperience?.markInlineResponse();
						stopInteractionMetricsTimeToFirstAnswerPart('stop');

						updateCurrentConversation({
							traces: [],
							messages: addAnswerPart(
								getConversationMessages(getState(), conversationId),
								answerId,
								streamMessage,
								answerIndex,
							),
						});
					}

					// FINAL_RESPONSE part processing
					if (isStreamResponse(streamMessage)) {
						ufoExperience?.mark('finalAnswerPart');
						const isEscalationAssumed = hasEscalationPluginWithExtraInfo(
							streamMessage.message.message.plugin_invocations,
						);
						updateCurrentConversation({
							traces: [],
							messages: replaceAnswerData(
								getConversationMessages(getState(), conversationId),
								answerId,
								streamMessage,
								answerIndex,
							),
							...(isEscalationAssumed && { channelStatus: 'ESCALATED' }), // TODO(CSMAI-2440): Remove once we have a status response
						});

						trackingAttributes.messageActions = streamMessage.message.message?.actions?.map(
							(action) => action.key,
						);

						if (streamMessage?.message?.message?.sources) {
							trackingAttributes.sources = streamMessage.message.message.sources
								.map((x) => x.ari)
								.filter(Boolean);
							trackingAttributes.usesJsm = false;
						}

						if (streamMessage.message?.message?.plugin_invocations) {
							streamMessage.message.message.plugin_invocations.forEach((pluginInvocation) => {
								if (pluginInvocation.plugin_name) {
									trackingAttributes.pluginsInvoked.add(pluginInvocation.plugin_name);
								}
								if (
									pluginInvocation.plugin_output &&
									containsServiceDeskSource(pluginInvocation.plugin_output)
								) {
									trackingAttributes.usesJsm = true;
								}
							});
						}

						const minionOrchestrationOutputs = streamMessage?.message?.message?.minion_outputs;

						minionOrchestrationOutputs &&
							minionOrchestrationOutputs.flat().forEach((output) => {
								trackingAttributes.pluginsInvoked.add(output.minion_id);
								if (output.minion_output && containsServiceDeskSource(output.minion_output)) {
									trackingAttributes.usesJsm = true;
								}
							});

						trackingAttributes.messageId = streamMessage?.message?.message?.id.toString();
						trackingAttributes.humanMessageId =
							streamMessage?.message?.human_message?.id.toString();
						trackingAttributes.hasSources = !!streamMessage?.message?.message?.sources?.length;

						try {
							const liveChatExtraInfo = getLiveChatPluginExtraInfo(
								streamMessage.message.message.plugin_invocations,
							);
							if (liveChatExtraInfo) {
								// Persist the twilioConversationId on the conversation in the store
								const currentConv = getState().conversations[conversationId];
								if (currentConv) {
									dispatch(
										setConversationData({
											conversationId,
											data: {
												twilioConversationId: liveChatExtraInfo.liveChatConversationId,
											} as Partial<OmniEnhancedConversation>,
										}),
									);
								}
								const ocgTwilioClientMod = await import(
									/* webpackChunkName: "@atlassian/conversation-assistant-widget/ocg-twilio-client" */ '../../extensions/twilio/ocg-twilio-client'
								);
								dispatch(initializeTwilioConversation(conversationId, ocgTwilioClientMod));
							}
						} catch (error) {
							logException({
								exception: error,
								name: 'ServerInitiatedLiveChatHandoffFailed',
							});
							throw error;
						}
					}

					if (isFollowUpMessage(streamMessage)) {
						ufoExperience?.mark('followUpMessage');
						updateCurrentConversation({
							traces: [],
							messages: [
								...getConversationMessages(getState(), conversationId),
								{
									type: 'FOLLOW_UP',
									content: streamMessage,
									id: randomIntFromInterval(0, 1000000),
								},
							],
						});
					}

					if (isCSMExceptionError(streamMessage)) {
						interactionMetrics.sendMessage.cancel();
						stopInteractionMetricsTimeToFirstAnswerPart('cancel');

						throw new StreamErrorException(
							`Stream - Unhandled CSM exception`,
							{
								message: { content: streamMessage.message },
								metadata: null,
							},
							streamMessage.name,
						);
					}

					if (isStreamPromptRedaction(streamMessage)) {
						const redactingTypes =
							(
								FeatureGates.getExperiment('rovo_chat_redacting_guard_config').value as {
									value?: string[];
								}
							)?.value ?? [];
						if (
							redactingTypes.includes(streamMessage.type) &&
							fg('rovo-guard-ui-redaction-and-blocking')
						) {
							guardEventReceived = true;
							// Remove the empty placeholder ASSISTANT message, then apply redaction to
							// the human message. The backend will not provide a real assistant response.
							const withoutPlaceholder = getConversationMessages(getState(), conversationId).filter(
								(m) =>
									!(isStoreAssistant(m) && m.content.message?.message?.id === String(answerId)),
							);
							const redactedMessages = applyRedactionToLatestHumanMessage(
								withoutPlaceholder,
								streamMessage.message.redacted_prompt,
							);
							updateCurrentConversation({ messages: redactedMessages });
						}
					}

					if (isStreamPromptBlock(streamMessage)) {
						const blockingTypes =
							(
								FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
									value?: string[];
								}
							)?.value ?? [];
						if (
							blockingTypes.includes(streamMessage.type) &&
							fg('rovo-guard-ui-redaction-and-blocking')
						) {
							guardEventReceived = true;
							// Remove the empty placeholder ASSISTANT message and replace it with
							// GUARD_BLOCK. The backend will not provide a real assistant response.
							const withoutPlaceholder = getConversationMessages(getState(), conversationId).filter(
								(_, idx) => idx !== answerIndex,
							);
							const guardBlockItem: StoreGuardBlock = {
								type: 'GUARD_BLOCK',
								id: String(genIntId()),
								time_created: new Date().toISOString(),
								blockType: streamMessage.type,
								traceId: streamMessage.metadata?.request_id ?? undefined,
							};
							updateCurrentConversation({
								messages: [...withoutPlaceholder, guardBlockItem],
							});
						}
					}

					if (isStreamResponseRedaction(streamMessage)) {
						const redactingTypes =
							(
								FeatureGates.getExperiment('rovo_chat_redacting_guard_config').value as {
									value?: string[];
								}
							)?.value ?? [];
						if (
							redactingTypes.includes(streamMessage.type) &&
							fg('rovo-guard-ui-redaction-and-blocking')
						) {
							// Do NOT set guardEventReceived — stream continues after response redaction.
							const messages = getConversationMessages(getState(), conversationId);
							updateCurrentConversation({
								messages: applyRedactionToLatestAssistantMessage(
									messages,
									streamMessage.message.redacted_response,
								),
							});
						}
					}

					if (isStreamResponseBlock(streamMessage)) {
						const blockingTypes =
							(
								FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
									value?: string[];
								}
							)?.value ?? [];
						if (
							blockingTypes.includes(streamMessage.type) &&
							fg('rovo-guard-ui-redaction-and-blocking')
						) {
							guardEventReceived = true;
							const currentMessages = getConversationMessages(getState(), conversationId);
							const withoutPlaceholder = currentMessages.filter((_, idx) => idx !== answerIndex);
							const guardBlockItem: StoreGuardBlock = {
								type: 'GUARD_BLOCK',
								id: String(genIntId()),
								time_created: new Date().toISOString(),
								blockType: streamMessage.type,
								traceId: streamMessage.metadata?.request_id ?? undefined,
							};
							updateCurrentConversation({
								messages: [...withoutPlaceholder, guardBlockItem],
							});
						}
					}

					if (isStreamAttachmentBlock(streamMessage)) {
						const blockingTypes =
							(
								FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
									value?: string[];
								}
							)?.value ?? [];
						if (
							blockingTypes.includes(streamMessage.type) &&
							fg('rovo-guard-ui-redaction-and-blocking')
						) {
							guardEventReceived = true;
							const withoutPlaceholder = getConversationMessages(getState(), conversationId).filter(
								(_, idx) => idx !== answerIndex,
							);
							const guardBlockItem: StoreGuardBlock = {
								type: 'GUARD_BLOCK',
								id: String(genIntId()),
								time_created: new Date().toISOString(),
								blockType: streamMessage.type,
								traceId: streamMessage.metadata?.request_id ?? undefined,
							};
							updateCurrentConversation({
								messages: [...withoutPlaceholder, guardBlockItem],
							});
						}
					}

					if (isStreamError(streamMessage)) {
						// If a guard event (PROMPT_REDACTION, PROMPT_BLOCKED, RESPONSE_BLOCKED, or ATTACHMENT_BLOCKED) was
						// already received, the backend always follows with an ERROR to terminate the stream.
						// Treat it as a normal end of the guard flow — break without throwing.
						if (guardEventReceived) {
							break;
						}

						const { message, metadata } = streamMessage;

						// AUP violations are still considered successful
						if (isStreamAUPError(streamMessage)) {
							experienceTracker.succeed({
								name: ExperienceName.SEND_MESSAGE,
							});
							ufoExperience?.success();
						}

						interactionMetrics.sendMessage.cancel();
						stopInteractionMetricsTimeToFirstAnswerPart('cancel');

						throw new StreamErrorException(`Stream - ${message?.content || 'Unknown'}`, {
							message,
							metadata,
						});
					}
				}
				/*****************************
				 *
				 * Stream processing END
				 *
				 *****************************/

				updateCurrentConversation({
					sourceAgentId: getSourceAgentId(getState(), conversationId),
				});

				sendAnalyticsEvent({
					eventType: 'track',
					action: 'sent',
					actionSubject: 'message',
					attributes: {
						messageId: trackingAttributes.humanMessageId,
						interactionSource: messageSources?.interactionSource,
						followUpSource: messageSources?.followUpSource ?? 'none',
						timestamp: messageSentTimeStamp,
						isClicked,
						[AI_MATE_ATTRIBUTES.conversationChannelId]: conversationId,
						isAtlassianAccount:
							conversationContextStoreActions.getConversationContext()?.isAtlassianAccount ?? null,
					},
				});

				// TODO: This should be behind the !aborted logic, but due to a bug in the BE service, aborted requests
				// will client connection with the server yet still complete in the background. This results in the UI showing
				// no message updates, until reload when the actual resolved message can be sesen.
				// Once that is fixed, we can place this behind the !aborted logic.
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'received',
					actionSubject: 'agentMessage',
					attributes: {
						// messageId must remain the ID received from the server in the FINAL_RESPONSE
						// in order to map correctly to our event funnel, e.g `agentMessage received` -> `agentResponse content copied`
						messageId: trackingAttributes.messageId,
						humanMessageId: trackingAttributes.humanMessageId,
						pluginsInvoked: Array.from(trackingAttributes.pluginsInvoked),
						requestId: trackingAttributes.requestId,
						hasSources: trackingAttributes.hasSources,
						responseSources: trackingAttributes.sources,
						usesJsm: trackingAttributes.usesJsm,
						timestamp: new Date().toISOString(),
						messageActions: trackingAttributes.messageActions,
						[AI_MATE_ATTRIBUTES.conversationChannelId]: conversationId,
					},
				});

				updateCurrentConversation(clearStatus());

				updateCurrentConversation({
					messages: markStreamingMessagesAsComplete(
						getConversationMessages(getState(), conversationId),
					),
				});

				// Only fire if the request wasn't aborted
				if (!abortController.signal.aborted) {
					initiateAIMAUEvent();
					trackAIMAUEvent({
						type: 'viewed',
						source: messageSources?.interactionSource,
						messageId: trackingAttributes.messageId,
						subjectId: messageSources?.followUpSource,
						proactive: false,
						userGenerated: true,
						citedProductList: {},
						sourceProducts: {},
						responseSources: trackingAttributes.sources,
						usesJsm: trackingAttributes.usesJsm,
						messageActions: trackingAttributes.messageActions,
						[AI_MATE_ATTRIBUTES.conversationChannelId]: conversationId,
					});

					// Experience is aborted in `abortRequests`
					experienceTracker.succeed({
						name: ExperienceName.SEND_MESSAGE,
					});

					ufoExperience?.success();

					eventHandlers.chatMessage.completed({
						conversationId,
						message: adfToText(prompt),
					});
				} else {
					ufoExperience?.abort();

					eventHandlers.chatMessage.aborted({
						conversationId,
						message: adfToText(prompt),
					});
				}

				interactionMetrics.sendMessage.stop();
			};

			const conversationId = conversationIdParam ?? getState().currentConversationId;

			try {
				await sendMessageFn({ conversationId });
			} catch (error: unknown) {
				if (isChannelNotFoundError(error)) {
					const createdChannel = await assistanceService.createConversationChannel();

					setState({
						conversations: {
							...getState().conversations,
							[createdChannel.id]: {
								id: createdChannel.id,
								name: createdChannel.name,
								timeCreated: createdChannel.time_created,
								messages: [],
								status: CHAT_STORE_STATUS.idle,
								traces: [],
							},
						},
					});

					dispatch(
						setSelectedConversation({
							conversationId: createdChannel.id,
						}),
					);

					await sendMessageFn({ conversationId: createdChannel.id });
				} else {
					if (isRateLimitedError(error)) {
						ufoExperience?.abort();
					} else {
						ufoExperience?.failure();
					}
					throw error;
				}
			}
		},
	{
		experienceName: ExperienceName.SEND_MESSAGE,
	},
);

/**
 * Fetches omni-core conversations and merges them into the store format.
 * Shared by both initConversationChannelsData and refetchConversations.
 */
const fetchAndMapOmniConversations = async (
	helpCenterId: string,
	existingConversations: Record<string, Conversation>,
	ocgClient: OcgClient,
): Promise<Record<string, OmniEnhancedConversation>> => {
	const response = await ocgClient.listOmniConversations(helpCenterId);
	return response.conversations.reduce<Record<string, OmniEnhancedConversation>>(
		(conversationsById, omniItem) => {
			const conversation = mapOmniConversationToStore(omniItem);
			if (conversation) {
				// Preserve existing in-memory conversation data if present.
				// Only override `messages` when the existing entry actually has
				// messages — otherwise keep the freshly-mapped empty array. This
				// avoids a TypeError when the conversation is brand new and not
				// yet in the in-memory store.
				const existingConversation = existingConversations[conversation.id];
				conversationsById[conversation.id] = {
					...existingConversation,
					...conversation,
					...(existingConversation?.messages?.length
						? { messages: existingConversation.messages }
						: {}),
				};
			}
			return conversationsById;
		},
		{},
	);
};

const refetchConversations = createSafeAction(
	(): ChatStoreAction =>
		async ({ getState, setState }, { csmAiHubId, assistanceService, customerHubId, ocgClient }) => {
			// When omni-channel live chat is enabled, use the unified omni-core endpoint
			// which returns all conversation types including direct live chat.
			if (customerHubId) {
				const helpCenterId = extractHelpCenterIdFromAri(customerHubId);
				if (helpCenterId) {
					try {
						const { conversations } = getState();
						const processedChannels = await fetchAndMapOmniConversations(
							helpCenterId,
							conversations,
							ocgClient,
						);

						// Preserve the empty conversation so unsent drafts are
						// never lost when the list is re-hydrated.
						setState({
							conversations: {
								EMPTY_CONVERSATION_ID: conversations[EMPTY_CONVERSATION_ID],
								...processedChannels,
							},
						});
					} catch (error) {
						logException({
							exception: error,
							name: 'RefetchOmniConversationsFailed',
						});
					}
					return;
				}
			}

			let allConversationsFromAPI;
			try {
				allConversationsFromAPI = await assistanceService.getConversationChannels(csmAiHubId);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (error?.response?.status === 401) {
					throw error;
				}
			}
			const { conversations } = getState();
			const updatedConversations = (allConversationsFromAPI ?? []).reduce<
				Record<string, Conversation>
			>((acc, channel) => {
				acc[channel.id] = {
					...conversations[channel.id],
					channelStatus: channel.status,
				};

				return acc;
			}, {});

			setState({
				conversations: {
					EMPTY_CONVERSATION_ID: conversations[EMPTY_CONVERSATION_ID],
					...updatedConversations,
				},
			});
		},
);

export const INITIAL_RELOADING_CHAT_TRACE_MESSAGE: Trace = { messageContent: 'Reloading chat' };

/**
 * Converts API chat messages into store items.
 * Filters to only include human and assistant (final response) messages.
 */
const convertChatToStoreItems = (chat: CsmChat): StoreChatItem[] => {
	const items: StoreChatItem[] = [];

	for (const message of chat) {
		if (isHumanMessage(message)) {
			const csmMediaAttachments = fg('csm_berry_2728_image_attachments_poc')
				? csmMediaAttachmentsFromFileIds(message.message_metadata?.fileIds ?? [])
				: undefined;
			items.push(createStoreHumanWithContent(message, csmMediaAttachments));
		} else if (isStreamResponse(message)) {
			items.push(createStoreAssistantWithContent(message));
		}
	}

	return items;
};

/**
 * Shared logic for processing API-fetched chat messages and merging them with
 * any existing store messages (including backfilled Twilio live chat messages).
 *
 * Used by both {@link fetchConversation} and {@link unsafeFetchConversation}
 * to avoid duplicating the backfill-aware merge logic.
 */
const processAndMergeFetchedChat = (
	serviceChat: CsmChat,
	conversationId: string,
	getState: () => WidgetChatStorePrivateState,
	dispatch: (action: ChatStoreAction) => void,
) => {
	// Get the most up to date conversation, in case there are multiple fetch operations going on
	const updatedConversation = getState().conversations[conversationId];
	const existingMessages = updatedConversation?.messages ?? [];

	// Identify CSM Twilio live chat messages (both agent and help-seeker) in the store.
	// These are excluded from the offset calculation because they were added via the
	// real-time listener or backfill, not from the API response.
	const backfilledLiveChatMessages = existingMessages.filter(isStoreTwilioLiveChatMessage);

	// Exclude backfilled messages from the offset so we only skip API-fetched messages
	// already in the store, not Twilio messages added via the real-time listener or backfill.
	const nonBackfillMessageCount = existingMessages.length - backfilledLiveChatMessages.length;
	const latestMessages =
		nonBackfillMessageCount > 0 ? serviceChat.slice(nonBackfillMessageCount) : serviceChat;

	const messages = convertChatToStoreItems(latestMessages);

	// Re-append backfilled live chat messages after API messages to maintain chronological order
	// (AI conversation messages first, then live chat messages).
	if (backfilledLiveChatMessages.length > 0) {
		messages.push(...backfilledLiveChatMessages);
	}

	// Use the non-backfill subset of existing messages as the base, so we preserve
	// messages from prior fetches without duplicating backfilled messages.
	// Uses the already-captured `existingMessages` snapshot to avoid race conditions
	// where backfill adds messages between our state reads.
	const backfilledLiveChatMessageSet = new Set<StoreChatItem>(backfilledLiveChatMessages);
	const baseMessages =
		backfilledLiveChatMessageSet.size > 0
			? existingMessages.filter((m) => !backfilledLiveChatMessageSet.has(m))
			: existingMessages;

	dispatch(
		setConversationData({
			conversationId,
			data: {
				...clearStatus(),
				messages: [...baseMessages, ...messages],
				sourceAgentId: getSourceAgentId(getState(), conversationId),
			},
		}),
	);
	return;
};

const getCurrentConversation =
	(): Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Conversation | undefined> =>
	({ getState }) => {
		return getState().conversations[getState().currentConversationId];
	};

const fetchConversation = createSafeAction(
	({ conversationId: conversationIdParam }: { conversationId?: string }): ChatStoreAction =>
		async ({ getState, dispatch }, { assistanceService, experienceTracker }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const conversation = getState().conversations[conversationId];

			if (conversation?.status !== CHAT_STORE_STATUS.idle || isEmptyConversation(conversationId)) {
				return;
			}

			// Direct live chat conversations (including the temporary pending
			// conversation used during initialization) have no ConvoAI backing.
			// Skip the assistanceService.getChat() call — messages for direct live
			// chat come from the Twilio SDK (real-time listener + backfill), not
			// from ConvoAI. The omni conversation list endpoint (BEARS-1256) now
			// populates the chat-history list; this guard prevents 400 Bad Request
			// errors that would occur when assistanceService.getChat() is called
			// with a direct live chat conversation ID that doesn't exist in
			// ConvoAI.
			if (isPendingDirectLiveChat(conversationId) || isDirectLiveChat(conversation)) {
				return;
			}

			experienceTracker.start({
				name: ExperienceName.FETCH_CHAT,
				id: conversationId,
				attributes: {
					conversationId,
				},
			});

			await conversation.storeAbortController?.abort();

			dispatch(
				initializeConversationData({
					conversationId,
					data: {
						id: conversationId,
						name: conversation.name,
						timeCreated: conversation.timeCreated,
						messages: conversation?.messages ?? [],
						status: CHAT_STORE_STATUS.loadingChat,
						traces: [INITIAL_RELOADING_CHAT_TRACE_MESSAGE],
						sourceAgentId: getSourceAgentId(getState(), conversationId),
						channelStatus: conversation.channelStatus,
					},
				}),
			);

			const serviceChat = await assistanceService.getChat(conversationId);

			processAndMergeFetchedChat(serviceChat, conversationId, getState, dispatch);

			experienceTracker.succeed({
				name: ExperienceName.FETCH_CHAT,
			});
		},
	{
		experienceName: ExperienceName.FETCH_CHAT,
	},
);

const unsafeFetchConversation =
	({ conversationId: conversationIdParam }: { conversationId?: string }): ChatStoreAction =>
	async ({ getState, dispatch }, { assistanceService }) => {
		const conversationId = conversationIdParam ?? getState().currentConversationId;

		const conversation = getState().conversations[conversationId];

		await conversation.storeAbortController?.abort();

		dispatch(
			initializeConversationData({
				conversationId,
				data: {
					id: conversationId,
					name: conversation.name,
					timeCreated: conversation.timeCreated,
					messages: conversation?.messages ?? [],
					status: CHAT_STORE_STATUS.loadingChat,
					traces: [INITIAL_RELOADING_CHAT_TRACE_MESSAGE],
					sourceAgentId: getSourceAgentId(getState(), conversationId),
					channelStatus: conversation.channelStatus,
				},
			}),
		);

		const serviceChat = await assistanceService.getChat(conversationId);

		processAndMergeFetchedChat(serviceChat, conversationId, getState, dispatch);
	};

const restoreConversation = createSafeAction(
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
		async ({ dispatch, getState }) => {
			const existingConversation = getState().conversations[conversationId];

			if (existingConversation) {
				await dispatch(setSelectedConversation({ conversationId }));
				return;
			}

			await dispatch(
				createConversationOptimistically({
					id: conversationId,
					status: CHAT_STORE_STATUS.loadingChat,
				}),
			);

			try {
				await dispatch(unsafeFetchConversation({ conversationId }));
			} catch {
				await dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));
			}
		},
);

// Abort requests for a specific conversation, or ALL conversations if no conversationId is passed
const abortRequests = createSafeAction(
	({ conversationId }: { conversationId?: string } = {}): ChatStoreAction =>
		({ getState }, { experienceTracker, trackAIMAUEvent }) => {
			const conversations = getState().conversations;

			const abortConversationStream = (conversation: Conversation) => {
				if (conversation?.storeAbortController) {
					experienceTracker.start({
						name: ExperienceName.ABORT_AGENT_STREAM,
						id: conversation?.id,
						attributes: {
							conversationId: conversation?.id,
						},
					});

					conversation.storeAbortController.abort();

					trackAIMAUEvent({
						type: 'initiated',
						messageId: getLastRequestIdFromConversation(conversation),
					});
					trackAIMAUEvent({
						type: 'dismissed',
						userGenerated: true,
						messageId: getLastRequestIdFromConversation(conversation),
					});

					experienceTracker.abort({
						name: ExperienceName.SEND_MESSAGE,
						reason: 'Aborted',
					});

					experienceTracker.succeed({
						name: ExperienceName.ABORT_AGENT_STREAM,
					});
				}
			};

			if (conversationId) {
				abortConversationStream(conversations[conversationId]);
			} else {
				Object.values(conversations).forEach(abortConversationStream);
			}
		},
	{
		experienceName: ExperienceName.ABORT_AGENT_STREAM,
	},
);

const initConversationChannelsData = createSafeAction(
	({
		autoSelectConversation = true,
	}: {
		autoSelectConversation?: boolean;
	} = {}): ChatStoreAction =>
		async (
			{ setState, getState },
			{ csmAiHubId, assistanceService, experienceTracker, customerHubId, ocgClient },
		) => {
			experienceTracker.start({
				id: ExperienceName.GET_CONVERSATION_CHANNELS,
				name: ExperienceName.GET_CONVERSATION_CHANNELS,
			});

			// When omni-channel live chat is enabled, use the unified omni-core endpoint
			// which returns all conversation types including direct live chat.
			if (customerHubId) {
				const helpCenterId = extractHelpCenterIdFromAri(customerHubId);
				if (helpCenterId) {
					const { conversations, currentConversationId } = getState();
					const processedChannels = await fetchAndMapOmniConversations(
						helpCenterId,
						conversations,
						ocgClient,
					).catch((error) => {
						logException({
							exception: error,
							name: 'InitOmniConversationsFailed',
						});
						experienceTracker.fail({
							name: ExperienceName.GET_CONVERSATION_CHANNELS,
							error: error instanceof Error ? error : new Error(String(error)),
						});
						// return empty 'channels map' when this request fails
						return {};
					});

					// Preserve the empty conversation so unsent drafts are
					// never lost when the list is re-hydrated.
					setState({
						conversations: {
							EMPTY_CONVERSATION_ID: conversations[EMPTY_CONVERSATION_ID],
							...processedChannels,
						},
					});

					const conversationIds = Object.keys(processedChannels);
					// The currently-selected conversation was deleted server-side if it
					// is a real (non-empty) conversation that no longer appears in the
					// freshly-fetched list. When this happens we need to auto-select
					// another conversation so the user isn't left viewing a stale one.
					const conversationDeleted =
						!isEmptyConversation(currentConversationId) &&
						!conversationIds.includes(currentConversationId);

					if (
						autoSelectConversation &&
						(conversationDeleted ||
							(isEmptyConversation(currentConversationId) &&
								!conversations[EMPTY_CONVERSATION_ID]?.sourceAgentId))
					) {
						if (conversationIds.length) {
							setState({ currentConversationId: conversationIds[0] });
						} else {
							setState({ currentConversationId: EMPTY_CONVERSATION_ID });
						}
					}

					experienceTracker.succeed({
						name: ExperienceName.GET_CONVERSATION_CHANNELS,
					});

					return;
				}
			}

			const channelsFromAPI = await assistanceService.getConversationChannels(csmAiHubId);
			const { conversations, currentConversationId } = getState();

			const processedChannels = channelsFromAPI.reduce<Record<string, Conversation>>(
				(acc, channel) => {
					acc[channel.id] = conversations[channel.id] ?? {
						name: channel.name ?? generateConversationName(),
						id: channel.id,
						messages: [],
						status: CHAT_STORE_STATUS.idle,
						traces: [],
						timeCreated: channel.time_created,
						lastMessageTimestamp: channel.last_message_timestamp,
						channelStatus: channel.status,
					};

					return acc;
				},
				{},
			);

			// Hydrate the store with the fetched conversation channels
			// whilst preserving the empty state, so drafts are never lost
			// due to rehydration (e.g window refocus) when this is called
			setState({
				conversations: {
					EMPTY_CONVERSATION_ID: conversations[EMPTY_CONVERSATION_ID],
					...processedChannels,
				},
			});

			// If conversation isn't empty state and it doesn't exist on the server, then assume deleted
			const conversationDeleted =
				!isEmptyConversation(currentConversationId) &&
				!channelsFromAPI.some((channel) => channel.id === currentConversationId);

			// LOGIC BEFORE AIM-2333 & AIM-2981
			// Slight adjustment to blend empty convo with deleted logic here
			// For backwards compatibility with the old behaviour, since some
			// playwright tests were failing since we always have an empty convo
			// in the list now
			// No sourceAgentId means pristine empty state
			if (
				autoSelectConversation &&
				(conversationDeleted ||
					(isEmptyConversation(currentConversationId) &&
						!conversations[EMPTY_CONVERSATION_ID]?.sourceAgentId))
			) {
				if (channelsFromAPI.length) {
					setState({
						currentConversationId: channelsFromAPI[0].id,
					});
				} else {
					setState({
						currentConversationId: EMPTY_CONVERSATION_ID,
					});
				}
			}

			experienceTracker.succeed({
				name: ExperienceName.GET_CONVERSATION_CHANNELS,
			});
		},
	{
		experienceName: ExperienceName.GET_CONVERSATION_CHANNELS,
	},
);

const createConversationOptimistically = createSafeAction(
	(options: {
		name?: string;
		sourceId?: string;
		id?: string;
		timeCreated?: string;
		lastMessageTimestamp?: string;
		sourceAgentId?: string;
		status?: CHAT_STORE_STATUS;
	}): ChatStoreAction =>
		async ({ dispatch }) => {
			const conversationId = options?.id ?? EMPTY_CONVERSATION_ID;
			const time = new Date().toISOString();

			dispatch(
				initializeConversationData({
					conversationId,
					data: {
						id: conversationId,
						lastMessageTimestamp: options.lastMessageTimestamp ?? time,
						messages: [],
						name: options.name ?? generateConversationName(),
						sourceAgentId: options.sourceAgentId,
						sourceId: options.sourceId ?? undefined,
						status: options?.status ?? CHAT_STORE_STATUS.idle,
						timeCreated: options?.timeCreated ?? time,
						traces: [],
					},
				}),
			);

			dispatch(
				setSelectedConversation({
					conversationId,
				}),
			);
		},
);

const setSelectedConversation = createSafeAction(
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
		({ getState, setState, dispatch }) => {
			if (!getState().chatMode && fg('live-chat-helpseeker-spotlight')) {
				const previousConversationId = getState().currentConversationId;

				if (previousConversationId && previousConversationId !== conversationId) {
					const previousConversation = getState().conversations[previousConversationId];
					const previousMessages = previousConversation?.messages;

					if (previousMessages?.some((message) => isStoreHuman(message) && !message.isHistorical)) {
						dispatch(
							setConversationData({
								conversationId: previousConversationId,
								data: {
									messages: previousMessages.map((message) =>
										isStoreHuman(message) && !message.isHistorical
											? { ...message, isHistorical: true }
											: message,
									),
								},
							}),
						);
					}
				}
			}

			// Select the conversation
			setState({
				currentConversationId: conversationId,
				...(conversationId === EMPTY_CONVERSATION_ID ? { hasInitiatedLiveChat: false } : {}),
			});
		},
);

const cleanupTwilioTokenRefresh = createSafeAction(
	(): ChatStoreAction => cleanupTwilioTokenRefreshImpl(),
);

const initializeTwilioConversation = createSafeAction(
	(conversationId?: string, ocgTwilioClientModule?: OcgTwilioClientModule): ChatStoreAction =>
		initializeTwilioConversationImpl({
			conversationId,
			ocgTwilioClientModule,
			initializeLiveChat,
		}),
);

const initializeDirectLiveChat = createSafeAction(
	({
		helpCenterId,
		metadata,
		initialMessage,
		conversationName,
	}: {
		helpCenterId: string;
		metadata?: Record<string, string>;
		/** Optional initial message (ADF DocNode) to render optimistically and send via Twilio. */
		initialMessage?: DocNode;
		/** Localized display name for the conversation (shown in chat history). */
		conversationName: string;
	}): ChatStoreAction =>
		initializeDirectLiveChatImpl({
			helpCenterId,
			metadata,
			initialMessage,
			conversationName,
		}),
);

const initializeLiveChat = createSafeAction(
	(
		payload: {
			remoteEntityId: string;
			conversationAri?: string;
		} | null,
	): ChatStoreAction =>
		({ setState }) => {
			setState({
				liveChatMetadata: payload,
			});
		},
);

const setLiveChatSubscriptionId = createSafeAction(
	(subscriptionId: string | null): ChatStoreAction =>
		({ setState }) => {
			setState({
				liveChatSubscriptionId: subscriptionId,
				...(subscriptionId === null
					? { liveChatConversationAri: null }
					: { hasInitiatedLiveChat: true }),
			});
		},
);

const setChatConfiguration = createSafeAction(
	(chatConfig: {
		chatMode: ChatMode;
		supportTeamFallbackMessage?: string | null;
	}): ChatStoreAction =>
		({ setState }) => {
			setState({
				chatMode: chatConfig.chatMode,
				supportTeamFallbackMessage: chatConfig.supportTeamFallbackMessage ?? null,
			});
		},
);

const setLiveChatConversationAri = createSafeAction(
	(conversationAri: string | null): ChatStoreAction =>
		({ setState }) => {
			setState({
				liveChatConversationAri: conversationAri,
			});
		},
);

const setCsmLiveChatSubscriptionId = createSafeAction(
	(subscriptionId: string | null): ChatStoreAction =>
		({ setState }) => {
			setState({
				csmLiveChatSubscriptionId: subscriptionId,
			});
		},
);

const closeLiveChat = createSafeAction(
	(): ChatStoreAction =>
		({ setState }) => {
			setState({
				liveChatSubscriptionId: null,
				liveChatConversationAri: null,
				csmLiveChatSubscriptionId: null,
			});
		},
);

const cleanupTwilioConversation = createSafeAction(
	(conversationId: string): ChatStoreAction => cleanupTwilioConversationImpl(conversationId),
);

/**
 * Marks a Twilio conversation as ended in the store by setting channelStatus to 'ENDED'
 * and clearing live chat metadata. Also cleans up Twilio resources for that conversation.
 * Called when the Twilio SDK fires a 'state' update with current === 'closed',
 * or when we detect on restore that the Twilio conversation is already closed.
 */
const markTwilioConversationEnded = createSafeAction(
	(conversationId: string): ChatStoreAction =>
		({ getState, setState, dispatch }) => {
			const { conversations } = getState();
			const conversation = conversations[conversationId];
			if (!conversation || conversation.channelStatus === 'ENDED') {
				return;
			}
			// Clear csmLiveChatSubscriptionId only when the ended conversation is
			// the current one — UI components (isLiveChatActive, close button,
			// end-chat flow) read this field directly without checking channelStatus.
			// We do NOT null out liveChatMetadata: the TwilioLiveChatManager effect
			// already gates on `!conversationIsEnded(currentConversation)` before
			// activating listeners, so channelStatus: 'ENDED' is sufficient there.
			const { currentConversationId } = getState();
			const isCurrentConversation = conversationId === currentConversationId;
			setState({
				conversations: {
					...conversations,
					[conversationId]: {
						...conversation,
						channelStatus: 'ENDED',
					},
				},
				...(isCurrentConversation && {
					csmLiveChatSubscriptionId: null,
				}),
			});
			dispatch(cleanupTwilioConversation(conversationId));
		},
);

const clearTwilioConversationsWithMetadata = createSafeAction(
	(): ChatStoreAction => clearTwilioConversationsWithMetadataImpl(),
);

const updateActionInvocationStatus = createSafeAction(
	({
		conversationId,
		messageId,
		invocationId,
		status,
	}: {
		conversationId: string;
		messageId: string;
		invocationId: string;
		status: MessageActionInvocationStatus;
	}): ChatStoreAction =>
		async ({ dispatch, getState }) => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const { messages } = currentConversation;
			const agentMessage = getAgentMessageById(messages, messageId);

			if (!agentMessage.result) {
				return;
			}

			const updatedMessages = [...messages];

			updatedMessages[agentMessage.index] = {
				...agentMessage.result,
				invokedActions: {
					...agentMessage.result.invokedActions,
					[invocationId]: status,
				},
			};

			dispatch(
				setConversationData({
					conversationId,
					data: { messages: updatedMessages },
				}),
			);
		},
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ResolveConversationActionArgs<TActionResult = any> = {
	conversationId: string;
	messageId: string;
	payload: MessageActionPayload;
	/** generic processor function for each action type that needs to return a response such as Jira issue creation */
	actionContentProcessor?: (message: AssistantMessage) => TActionResult;
	systemMessages: SystemMessages;
};

const sendMessageConfirmedTools = createSafeAction(
	({
		conversationId: conversationIdParam,
		agentId,
		confirmed_tools,
		messageSources,
		storeUserMessages = false,
		webSearchEnabled,
		deepResearchEnabled,
		thinkDeeperEnabled,
		alphaExtendedThinkingMode,
		fastModeEnabled,
	}: {
		conversationId?: string;
		agentId?: string;
		confirmed_tools: RovoLabsConfirmedTools;
		messageSources?: Partial<MessageSource>;
		storeUserMessages?: boolean;
		webSearchEnabled?: boolean;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
		fastModeEnabled?: boolean;
	}): ChatStoreAction =>
		async ({ getState, dispatch }, { assistanceService, experienceTracker, trackAIMAUEvent }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			// Collection of callbacks for experience and performance tracking
			const expAndPerfTrackers = {
				start: () => {
					experienceTracker.start({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
						id: conversationId,
						attributes: {
							conversationId,
							interactionSource: messageSources?.interactionSource,
						},
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.start();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.start();
				},
				firstAnswerPartReceived: () => {
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
				},
				succeed: () => {
					experienceTracker.succeed({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
				},
				aupError: () => {
					experienceTracker.succeed({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
				},
				agentDeactivated: ({ error }: { error: ApiError }) => {
					experienceTracker.fail({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
						error,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.cancel();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.cancel();
				},
				abort: () => {
					experienceTracker.abort({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
						reason: 'Aborted',
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.cancel();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.cancel();
				},
				fail: ({ error }: { error: Error }) => {
					experienceTracker.fail({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
						error: error,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
				},
			};

			const trackingAttributes: {
				requestId?: string;
				messageId?: string;
				humanMessageId?: string;
				instrumentationId?: string;
				initiatedTimestamp?: string;
			} = {};
			// Collection of callbacks for analytics tracking
			const analyticsTrackers = {
				initiated: ({ timestamp }: { timestamp?: string }) => {
					trackAIMAUEvent({
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						timestamp,
					});
				},
				dismissed: () => {
					trackAIMAUEvent({
						type: 'dismissed',
						proactive: false,
						userGenerated: true,
						messageId: trackingAttributes.messageId,
						singleInstrumentationID: trackingAttributes.instrumentationId,
					});
				},
				viewed: () => {
					trackAIMAUEvent({
						type: 'viewed',
						proactive: false,
						userGenerated: true,
						source: messageSources?.interactionSource,
						messageId: trackingAttributes.messageId,
						subjectId: messageSources?.followUpSource,
						citedProductList: {},
						sourceProducts: {},
						singleInstrumentationID: trackingAttributes.instrumentationId,
						timestamp: new Date().toISOString(),
					});
				},
				error: () => {
					trackAIMAUEvent({
						type: 'error',
						proactive: false,
						userGenerated: true,
						messageId: trackingAttributes.requestId,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						timestamp: new Date().toISOString(),
					});
				},
			};

			const updateCurrentConversation = (data: Partial<Conversation>) => {
				dispatch(
					setConversationData({
						conversationId,
						data,
					}),
				);
			};

			const { abortController, storeAbortController, confirmAbort } = createStoreAbortController();

			let hasReceivedFinalResponse = false;
			let generator: AsyncGenerator<StreamMessage | ParsedSSEMessage<StreamMessage>> | undefined;
			let responseData: FetchStreamReturn<StreamMessage, boolean>['responseData'] | undefined;

			// Extract invocation IDs for status updates
			const toolInvocationIds = confirmed_tools.tools.map((tool) => tool.invocation_id);

			try {
				expAndPerfTrackers.start();

				// change the conversation status
				updateCurrentConversation({
					storeAbortController,
					status: CHAT_STORE_STATUS.awaitingResponse,
				});

				// Only add assistant message
				const tmpAssistantMessageId = generateTmpMessageId(conversationId);
				dispatch(
					addAssistantMessage({
						id: tmpAssistantMessageId,
						content: '',
						conversationId,
						agentNamedId: agentId,
					}),
				);
				dispatch(clearFollowUpMessages({ conversationId }));
				trackingAttributes.messageId = tmpAssistantMessageId;
				trackingAttributes.instrumentationId = tmpAssistantMessageId;

				// Assumed the insert is successful and inserts at the last position
				const answerIndex = getState().conversations[conversationId].messages.length - 1;

				// Update action status to 'started' for all tools in the confirmed_tools
				toolInvocationIds.forEach((invocationId) => {
					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId: confirmed_tools.message_id,
							invocationId,
							status: 'started',
						}),
					);
				});

				// Call the BE endpoint
				trackingAttributes.initiatedTimestamp = new Date().toISOString();
				const response = await assistanceService.sendMessageConfirmedTools({
					conversationId,
					agentId,
					confirmed_tools,
					controller: abortController,
					optInSSEStream: true,
					storeMessage: storeUserMessages,
					webSearchEnabled,
					deepResearchEnabled,
					thinkDeeperEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
				});
				generator = response.generator;
				responseData = response.responseData;

				/**
				 * --------
				 * Stream processing START
				 * --------
				 */

				updateCurrentConversation({
					status: CHAT_STORE_STATUS.typingResponse,
				});

				// Set to true when a guard event is received so the subsequent ERROR is suppressed.
				let guardEventReceived = false;

				for await (const streamObj of generator) {
					// Unwrap SSE message if needed (SSE returns ParsedSSEMessage, JSON fallback returns StreamMessage)
					const streamMessage = isSSEMessage(streamObj) ? streamObj.data : streamObj;
					/**
					 * Trace processing
					 */
					if (isStreamTrace(streamMessage)) {
						if (!trackingAttributes.requestId) {
							trackingAttributes.requestId = streamMessage.metadata?.request_id;
						}

						updateCurrentConversation({
							traces: dedupeTraceMessagesWithLatest([
								...(getState().conversations[conversationId]?.traces ?? []),
								getTraceFromStreamMessage(streamMessage),
							]),
						});
					}

					/**
					 * Answer part processing
					 */
					if (isStreamAnswerPart(streamMessage)) {
						expAndPerfTrackers.firstAnswerPartReceived();

						updateCurrentConversation({
							traces: [],
							messages: addAnswerPart(
								getConversationMessages(getState(), conversationId),
								tmpAssistantMessageId,
								streamMessage,
								answerIndex,
							),
						});
					}

					/**
					 * Final response processing
					 */
					if (isStreamResponse(streamMessage)) {
						hasReceivedFinalResponse = true;

						const humanMessageId = streamMessage?.message?.human_message?.id.toString();
						const agentMessageId = streamMessage?.message?.message?.id.toString();

						updateCurrentConversation({
							traces: [],
							messages: replaceAnswerData(
								getConversationMessages(getState(), conversationId),
								tmpAssistantMessageId,
								streamMessage,
								answerIndex,
							),
						});

						trackingAttributes.messageId = agentMessageId;
						trackingAttributes.humanMessageId = humanMessageId;
						trackingAttributes.instrumentationId = agentMessageId;
					}

					/**
					 * Follow up processing
					 */
					if (isFollowUpMessage(streamMessage)) {
						updateCurrentConversation({
							traces: [],
							messages: [
								...getConversationMessages(getState(), conversationId),
								{
									type: 'FOLLOW_UP',
									content: streamMessage,
									id: randomIntFromInterval(0, 1000000),
									humanMessageId: trackingAttributes.humanMessageId,
								},
							],
						});
					}

					if (isStreamPromptRedaction(streamMessage)) {
						const redactingTypes =
							(
								FeatureGates.getExperiment('rovo_chat_redacting_guard_config').value as {
									value?: string[];
								}
							)?.value ?? [];
						if (
							redactingTypes.includes(streamMessage.type) &&
							fg('rovo-guard-ui-redaction-and-blocking')
						) {
							guardEventReceived = true;
							const withoutPlaceholder = getConversationMessages(getState(), conversationId).filter(
								(m) =>
									!(
										isStoreAssistant(m) && m.content.message?.message?.id === tmpAssistantMessageId
									),
							);
							const redactedMessages = applyRedactionToLatestHumanMessage(
								withoutPlaceholder,
								streamMessage.message.redacted_prompt,
							);
							updateCurrentConversation({ messages: redactedMessages });
						}
					}

					if (isStreamPromptBlock(streamMessage)) {
						const blockingTypes =
							(
								FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
									value?: string[];
								}
							)?.value ?? [];
						if (
							blockingTypes.includes(streamMessage.type) &&
							fg('rovo-guard-ui-redaction-and-blocking')
						) {
							guardEventReceived = true;
							const withoutPlaceholder = getConversationMessages(getState(), conversationId).filter(
								(_, idx) => idx !== answerIndex,
							);
							const guardBlockItem: StoreGuardBlock = {
								type: 'GUARD_BLOCK',
								id: String(genIntId()),
								time_created: new Date().toISOString(),
								blockType: streamMessage.type,
								traceId: streamMessage.metadata?.request_id ?? undefined,
							};
							updateCurrentConversation({
								messages: [...withoutPlaceholder, guardBlockItem],
							});
						}
					}

					if (isStreamResponseRedaction(streamMessage)) {
						const redactingTypes =
							(
								FeatureGates.getExperiment('rovo_chat_redacting_guard_config').value as {
									value?: string[];
								}
							)?.value ?? [];
						if (
							redactingTypes.includes(streamMessage.type) &&
							fg('rovo-guard-ui-redaction-and-blocking')
						) {
							// Do NOT set guardEventReceived — stream continues after response redaction.
							const messages = getConversationMessages(getState(), conversationId);
							updateCurrentConversation({
								messages: applyRedactionToLatestAssistantMessage(
									messages,
									streamMessage.message.redacted_response,
								),
							});
						}
					}

					if (isStreamResponseBlock(streamMessage)) {
						const blockingTypes =
							(
								FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
									value?: string[];
								}
							)?.value ?? [];
						if (
							blockingTypes.includes(streamMessage.type) &&
							fg('rovo-guard-ui-redaction-and-blocking')
						) {
							guardEventReceived = true;
							const currentMessages = getConversationMessages(getState(), conversationId);
							const withoutPlaceholder = currentMessages.filter((_, idx) => idx !== answerIndex);
							const guardBlockItem: StoreGuardBlock = {
								type: 'GUARD_BLOCK',
								id: String(genIntId()),
								time_created: new Date().toISOString(),
								blockType: streamMessage.type,
								traceId: streamMessage.metadata?.request_id ?? undefined,
							};
							updateCurrentConversation({
								messages: [...withoutPlaceholder, guardBlockItem],
							});
						}
					}

					if (isStreamAttachmentBlock(streamMessage)) {
						const blockingTypes =
							(
								FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
									value?: string[];
								}
							)?.value ?? [];
						if (
							blockingTypes.includes(streamMessage.type) &&
							fg('rovo-guard-ui-redaction-and-blocking')
						) {
							guardEventReceived = true;
							const withoutPlaceholder = getConversationMessages(getState(), conversationId).filter(
								(_, idx) => idx !== answerIndex,
							);
							const guardBlockItem: StoreGuardBlock = {
								type: 'GUARD_BLOCK',
								id: String(genIntId()),
								time_created: new Date().toISOString(),
								blockType: streamMessage.type,
								traceId: streamMessage.metadata?.request_id ?? undefined,
							};
							updateCurrentConversation({
								messages: [...withoutPlaceholder, guardBlockItem],
							});
						}
					}

					/**
					 * Error processing
					 */
					if (isStreamError(streamMessage)) {
						// If a guard event (PROMPT_REDACTION, PROMPT_BLOCKED, RESPONSE_BLOCKED, or ATTACHMENT_BLOCKED) was
						// already received, the backend always follows with an ERROR to terminate the stream.
						// Treat it as a normal end of the guard flow — break without throwing.
						if (guardEventReceived) {
							break;
						}

						const { message, metadata } = streamMessage;

						// AUP violations are still considered successful
						if (isStreamAUPError(streamMessage)) {
							expAndPerfTrackers.aupError();
						}

						throw new StreamErrorException(`Stream - ${message?.content || 'Unknown'}`, {
							message,
							metadata,
						});
					}
				}
			} catch (error) {
				if (
					error instanceof ApiError &&
					error.errorAttributes.errorCode?.includes(
						ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED,
					)
				) {
					expAndPerfTrackers.agentDeactivated({ error });
					updateCurrentConversation({
						status: CHAT_STORE_STATUS.deactivatedAgentError,
						traces: [],
					});

					analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
					analyticsTrackers.error();
					return;
				}

				if (error instanceof Error && error.name === 'AbortError') {
					expAndPerfTrackers.abort();
					confirmAbort();
					analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
					analyticsTrackers.dismissed();
					return;
				}

				expAndPerfTrackers.fail({
					error: error instanceof Error ? error : new Error(String(error)),
				});
				analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
				analyticsTrackers.error();
				throw error;
			} finally {
				updateCurrentConversation(clearStatus());
				updateCurrentConversation({
					messages: markStreamingMessagesAsComplete(
						getConversationMessages(getState(), conversationId),
					),
				});
			}

			/**
			 * --------
			 * Stream processing END
			 * --------
			 */

			// if stream finishes without FINAL_RESPONSE, BE is hanging
			if (!hasReceivedFinalResponse) {
				const error = new NoFinalResponseError(
					'The response took too long and timed out. Try refreshing the page.',
					{
						traceId: responseData?.traceId,
					},
				);
				experienceTracker.fail({
					name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
					error,
				});
				analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
				analyticsTrackers.error();
				throw error;
			}

			// Update action status to 'finished' for all tools in the confirmed_tools
			toolInvocationIds.forEach((invocationId) => {
				dispatch(
					updateActionInvocationStatus({
						conversationId,
						messageId: confirmed_tools.message_id,
						invocationId,
						status: 'finished',
					}),
				);
			});

			analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
			analyticsTrackers.viewed();
			expAndPerfTrackers.succeed();
		},
);

const resolveConversationAction = createSafeAction<
	(args: ResolveConversationActionArgs) => unknown,
	undefined
>(
	({ conversationId, messageId, payload, actionContentProcessor, systemMessages }) =>
		async <TActionResult>(
			{ dispatch, getState }: StoreActionApi<WidgetChatStorePrivateState>,
			{
				assistanceService,
				experienceTracker,
				trackAIMAUEvent,
				sendAnalyticsEvent,
				ufoExperiences,
			}: ChatStoreContainerProps,
		): Promise<TActionResult | undefined> => {
			let actionCallbackData: TActionResult | undefined;
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const attributes = {
				conversationId,
				resolution: payload.context.resolution,
			};

			// Check if this is a JSM live chat action and set up UFO tracking
			const isJsmLiveChatAction = payload.context.key === 'jsm-livechat';
			const ufoExperience =
				isJsmLiveChatAction && ufoExperiences && 'INITIALIZE_LIVE_CHAT' in ufoExperiences
					? ufoExperiences['INITIALIZE_LIVE_CHAT'].getInstance(conversationId)
					: undefined;

			// Start UFO experience tracking for JSM live chat
			if (isJsmLiveChatAction) {
				ufoExperience?.start();
			}

			experienceTracker.start({
				name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
				id: conversationId,
				attributes,
			});

			try {
				const { invocationId } = payload.context;

				// Set actions loading state
				dispatch(
					updateActionInvocationStatus({
						conversationId,
						messageId,
						invocationId,
						status: 'started',
					}),
				);

				const response = await assistanceService.resolveConversationAction(conversationId, payload);
				const { message } = response;

				// Display a new agent message from the response to reflect the change in chat history
				if (isAssistantMessage(message) && !!message.content) {
					const availabilityOutcome =
						message?.message_metadata?.actionOutput?._originalResponse?.availability?.outcome;

					const isLiveChatPaused = isJsmLiveChatAction && availabilityOutcome === 'PAUSED';

					const isAgentUnavailable = isJsmLiveChatAction && availabilityOutcome === 'NOT_AVAILABLE';

					if (isLiveChatPaused) {
						const originalResponse = message?.message_metadata?.actionOutput?._originalResponse;
						const issueKey = originalResponse?.issueKey;
						const requestUrl = originalResponse?.requestUrl;

						const pausedContent =
							issueKey && requestUrl
								? buildWithIssueMessage({
										prefix: systemMessages.liveChatPausedWithIssuePrefix,
										suffix: systemMessages.withIssueSuffix,
										issueKey,
										requestUrl,
									})
								: systemMessages.liveChatPaused;

						const pausedMessageId = generateTmpMessageId(conversationId);
						dispatch(
							addAssistantMessage({
								agentNamedId: message.author?.named_id,
								content: pausedContent,
								conversationId,
								id: pausedMessageId,
							}),
						);
					} else if (isAgentUnavailable) {
						const originalResponse = message?.message_metadata?.actionOutput?._originalResponse;
						const issueKey = originalResponse?.issueKey;
						const requestUrl = originalResponse?.requestUrl;

						const unavailableContent = getAgentUnavailableContent({
							issueKey,
							requestUrl,
							systemMessages,
						});

						const unavailableMessageId = generateTmpMessageId(conversationId);
						dispatch(
							addAssistantMessage({
								agentNamedId: message.author?.named_id,
								content: unavailableContent,
								conversationId,
								id: unavailableMessageId,
							}),
						);
					} else {
						dispatch(
							addAssistantMessage({
								agentNamedId: message.author?.named_id,
								content: message.content,
								conversationId,
								id: message.id,
							}),
						);
					}

					actionCallbackData = actionContentProcessor?.(message) ?? undefined;

					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId,
							invocationId,
							status: 'finished',
						}),
					);

					if (fg('live-chat-rovo-for-service')) {
						const isJsmLiveChat = payload.context.key === 'jsm-livechat';
						const messageMetadata = message?.message_metadata;
						const isSuccess = messageMetadata?.action_execution_result === 'SUCCESS';

						const remoteEntityId = messageMetadata?.actionOutput?._originalResponse?.channelAri;
						const conversationAri =
							messageMetadata?.actionOutput?._originalResponse?.conversationAri;
						const hasRemoteEntityId = !!remoteEntityId;

						const availability = messageMetadata?.actionOutput?._originalResponse?.availability;
						const availabilityOutcome = availability?.outcome;
						const availabilityReason = availability?.reason;
						const availabilityCriteria = availability?.criteria;

						// If this is a successful JSM live chat action, set pending live chat initialization
						// A component will listen to this state and call the live chat context

						if (
							isJsmLiveChat &&
							hasRemoteEntityId &&
							isSuccess &&
							!isAgentUnavailable &&
							!isLiveChatPaused
						) {
							dispatch(
								initializeLiveChat({
									remoteEntityId,
									conversationAri,
								}),
							);

							// Mark UFO experience as success - live chat initialized
							ufoExperience?.success();

							sendAnalyticsEvent({
								eventType: 'ui',
								action: 'initializeLiveChat',
								actionSubject: 'liveChatSubscription',
								attributes: {
									remoteEntityId,
									conversationAri,
									actionKey: payload.context.key,
									availabilityOutcome,
									availabilityReason,
									availabilityCriteria,
								},
							});
						} else if (isJsmLiveChat && isLiveChatPaused) {
							// JSM live chat action was attempted but live chat is paused by an admin.
							ufoExperience?.success();

							sendAnalyticsEvent({
								eventType: 'track',
								action: 'liveChatPaused',
								actionSubject: 'liveChatSubscription',
								attributes: {
									remoteEntityId,
									conversationAri,
									actionKey: payload.context.key,
									availabilityOutcome,
									availabilityReason,
									availabilityCriteria,
								},
							});
						} else if (isJsmLiveChat && isAgentUnavailable) {
							// JSM live chat action was attempted but agents are unavailable
							ufoExperience?.success();

							sendAnalyticsEvent({
								eventType: 'track',
								action: 'agentUnavailable',
								actionSubject: 'liveChatSubscription',
								attributes: {
									remoteEntityId,
									conversationAri,
									actionKey: payload.context.key,
									availabilityOutcome,
									availabilityReason,
									availabilityCriteria,
								},
							});
						} else if (isJsmLiveChat) {
							// JSM live chat action was attempted but failed conditions
							ufoExperience?.failure();
						}
					}

					sendAnalyticsEvent({
						eventType: 'ui',
						action: 'success',
						actionSubject: 'rovoChatMessageAction',
						attributes: {
							recipientAgentNamedId: payload.context.recipient_agent_named_id,
							actionKey: payload.context.key,
							resolutionType: payload.context.resolution.type,
						},
					});
				} else {
					// Mark UFO experience as failure for JSM live chat - invalid response
					ufoExperience?.failure();

					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId,
							invocationId: payload.context.invocationId,
							status: 'errored',
						}),
					);

					sendAnalyticsEvent({
						eventType: 'track',
						action: 'error',
						actionSubject: 'rovoChatMessageAction',
						attributes: {
							recipientAgentNamedId: payload.context.recipient_agent_named_id,
							actionKey: payload.context.key,
							resolutionType: payload.context.resolution.type,
						},
					});
				}
			} catch (error: unknown) {
				// Mark UFO experience as failure for JSM live chat
				ufoExperience?.failure();

				experienceTracker.fail({
					name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
					error: error instanceof Error ? error : new Error('Unknown error'),
					attributes,
				});

				trackAIMAUEvent({
					type: 'error',
					userGenerated: true,
					source: 'messageActionsResolver',
					subjectId: payload.context.resolution.type,
				});

				sendAnalyticsEvent({
					eventType: 'track',
					action: 'error',
					actionSubject: 'rovoChatMessageAction',
					attributes: {
						recipientAgentNamedId: payload.context.recipient_agent_named_id,
						actionKey: payload.context.key,
						resolutionType: payload.context.resolution.type,
					},
				});

				dispatch(
					updateActionInvocationStatus({
						conversationId,
						messageId,
						invocationId: payload.context.invocationId,
						status: 'errored',
					}),
				);

				throw error;
			}

			experienceTracker.succeed({
				name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
				attributes,
			});
			return actionCallbackData;
		},
);

/**
 * ************************************************************
 * Store
 * ************************************************************
 */

const actions: {
	abortRequests: (
		args_0?:
			| {
					conversationId?: string;
			  }
			| undefined,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	addLiveChatMessage: (
		args_0: LiveChatMessage,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	addTwilioLiveChatHumanMessage: (
		params: AddTwilioLiveChatMessageParams,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	addTwilioLiveChatMessage: (
		params: AddTwilioLiveChatMessageParams,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	backfillLiveChatMessages: (
		params: BackfillLiveChatMessagesParams,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	cleanupTwilioTokenRefresh: () => Action<
		WidgetChatStorePrivateState,
		ChatStoreContainerProps,
		Promise<void>
	>;
	clearFollowUpMessages: (args_0: {
		conversationId?: string;
	}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	cleanupTwilioConversation: (
		conversationId: string,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	markTwilioConversationEnded: (
		conversationId: string,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	clearTwilioConversationsWithMetadata: () => Action<
		WidgetChatStorePrivateState,
		ChatStoreContainerProps,
		Promise<void>
	>;
	closeLiveChat: () => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	createConversationOptimistically: (options: {
		name?: string;
		sourceId?: string;
		id?: string;
		timeCreated?: string;
		lastMessageTimestamp?: string;
		sourceAgentId?: string;
		status?: CHAT_STORE_STATUS;
	}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	getChat: (args_0: {
		conversationId?: string;
	}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	getCurrentConversation: () => Action<
		WidgetChatStorePrivateState,
		ChatStoreContainerProps,
		Conversation | undefined
	>;
	initConversationChannelsData: (args_0?: {
		autoSelectConversation?: boolean;
	}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	initializeDirectLiveChat: (args_0: {
		helpCenterId: string;
		metadata?: Record<string, string>;
		/** Optional initial message (ADF DocNode) to render optimistically and send via Twilio. */
		initialMessage?: DocNode;
		/** Localized display name for the conversation (shown in chat history). */
		conversationName: string;
	}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	initializeLiveChat: (
		payload: {
			remoteEntityId: string;
			conversationAri?: string;
		} | null,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	initializeTwilioConversation: (
		conversationId?: string | undefined,
		ocgTwilioClientModule?: typeof import('../../extensions/twilio/ocg-twilio-client') | undefined,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	resolveConversationAction: (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		args: ResolveConversationActionArgs<any>,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
	sendMessage: (
		args_0: {
			prompt: DocNode;
			conversationId?: string;
			storeUserMessages: boolean;
			messageSources?: Partial<MessageSource>;
			sendEditorContext?: boolean;
			isClicked?: boolean;
			liveChatMessageOnly?: boolean;
			csmMediaAttachments?: CsmMediaAttachment[];
		} & SendMessageAgentParam,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	sendMessageConfirmedTools: (args_0: {
		conversationId?: string;
		agentId?: string;
		confirmed_tools: RovoLabsConfirmedTools;
		messageSources?: Partial<MessageSource>;
	}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	setChatConfiguration: (chatConfig: {
		chatMode: ChatMode;
		supportTeamFallbackMessage?: string | null;
	}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	setLiveChatSubscriptionId: (
		subscriptionId: string | null,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	setLiveChatConversationAri: (
		conversationAri: string | null,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	setCsmLiveChatSubscriptionId: (
		subscriptionId: string | null,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	setSelectedConversation: (args_0: {
		conversationId: string;
	}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	refetchConversations: () => Action<
		WidgetChatStorePrivateState,
		ChatStoreContainerProps,
		Promise<void>
	>;
	restoreConversation: (args_0: {
		conversationId: string;
	}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	addTwilioAgentTyping: (
		params: TwilioAgentTypingParams,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	removeTwilioAgentTyping: (
		params: TwilioAgentTypingParams,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	clearTwilioAgentsTyping: () => Action<
		WidgetChatStorePrivateState,
		ChatStoreContainerProps,
		Promise<void>
	>;
	setTwilioAgentJoined: (
		params: SetTwilioAgentJoinedParams,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	hydrateTwilioConversation: (
		params: HydrateTwilioConversationParams,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	updateTwilioUnreadMessagesCount: (
		params: UpdateTwilioUnreadMessagesCountParams,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	checkInitialUnreadMessages: (
		params: CheckInitialUnreadMessagesParams,
	) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
} = {
	abortRequests,
	addLiveChatMessage,
	addTwilioLiveChatHumanMessage,
	addTwilioLiveChatMessage,
	backfillLiveChatMessages,
	cleanupTwilioTokenRefresh,
	clearFollowUpMessages,
	cleanupTwilioConversation,
	markTwilioConversationEnded,
	clearTwilioConversationsWithMetadata,
	closeLiveChat,
	createConversationOptimistically,
	getChat: fetchConversation,
	getCurrentConversation,
	initConversationChannelsData,
	initializeDirectLiveChat,
	initializeLiveChat,
	initializeTwilioConversation,
	resolveConversationAction,
	sendMessage,
	sendMessageConfirmedTools,
	setChatConfiguration,
	setLiveChatSubscriptionId,
	setLiveChatConversationAri,
	setCsmLiveChatSubscriptionId,
	setSelectedConversation,
	refetchConversations,
	restoreConversation,
	addTwilioAgentTyping,
	removeTwilioAgentTyping,
	clearTwilioAgentsTyping,
	setTwilioAgentJoined,
	hydrateTwilioConversation,
	updateTwilioUnreadMessagesCount,
	checkInitialUnreadMessages,
};

const chatStore = createChatStore(actions);

export type ChatStoreActions = typeof actions;

const chatStoreContainerOptions = {};

export const ChatStoreContainer: OverrideContainerComponent<ChatStoreContainerProps> =
	createContainer<WidgetChatStorePrivateState, ChatStoreActions, ChatStoreContainerProps>(
		chatStore,
		chatStoreContainerOptions,
	);

/**
 * ************************************************************
 * Main Hook
 * ************************************************************
 */

const publicStateSelector = (state: WidgetChatStorePrivateState): ChatStoreState => {
	return {
		...state,
		getMessageHistory: ({
			conversationId,
			startFromMessageId,
			count,
		}: {
			conversationId?: string;
			startFromMessageId?: string;
			count?: number;
		}) =>
			getMessageHistory(
				state.conversations[conversationId ?? state.currentConversationId]?.messages ?? [],
				startFromMessageId,
				count,
			),
		checkActiveConversationMatchesSource: (id?: string) => {
			return checkActiveConversationMatchesSource(state, id);
		},
		liveChatMetadata: state.liveChatMetadata || null,
		liveChatSubscriptionId: state.liveChatSubscriptionId || null,
		liveChatConversationAri: state.liveChatConversationAri || null,
		csmLiveChatSubscriptionId: state.csmLiveChatSubscriptionId || null,
		hasInitiatedLiveChat: state.hasInitiatedLiveChat ?? false,
		twilioClientIdentity: state.twilioClientTokenMeta?.identity || null,
		twilioConversationsWithMetadata: state.twilioConversationsWithMetadata || {},
		twilioAgentsTyping: state.twilioAgentsTyping || [],
		twilioConversationAgentJoined: state.twilioConversationAgentJoined || {},
		directLiveChatNoAgentAvailable: state.directLiveChatNoAgentAvailable ?? false,
		chatMode: state.chatMode ?? null,
		supportTeamFallbackMessage: state.supportTeamFallbackMessage ?? null,
	};
};

export const useChatStore: HookFunction<
	ChatStoreState,
	BoundActions<
		WidgetChatStorePrivateState,
		{
			abortRequests: (
				args_0?:
					| {
							conversationId?: string;
					  }
					| undefined,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addLiveChatMessage: (
				args_0: LiveChatMessage,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addTwilioLiveChatHumanMessage: (
				params: AddTwilioLiveChatMessageParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addTwilioLiveChatMessage: (
				params: AddTwilioLiveChatMessageParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			backfillLiveChatMessages: (
				params: BackfillLiveChatMessagesParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			cleanupTwilioTokenRefresh: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			clearFollowUpMessages: (args_0: {
				conversationId?: string;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			cleanupTwilioConversation: (
				conversationId: string,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			markTwilioConversationEnded: (
				conversationId: string,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			clearTwilioConversationsWithMetadata: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			closeLiveChat: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			createConversationOptimistically: (options: {
				name?: string;
				sourceId?: string;
				id?: string;
				timeCreated?: string;
				lastMessageTimestamp?: string;
				sourceAgentId?: string;
				status?: CHAT_STORE_STATUS;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			getChat: (args_0: {
				conversationId?: string;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			getCurrentConversation: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Conversation | undefined
			>;
			initConversationChannelsData: (args_0?: {
				autoSelectConversation?: boolean;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			initializeDirectLiveChat: (args_0: {
				helpCenterId: string;
				metadata?: Record<string, string>;
				/** Optional initial message (ADF DocNode) to render optimistically and send via Twilio. */
				initialMessage?: DocNode;
				/** Localized display name for the conversation (shown in chat history). */
				conversationName: string;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			initializeLiveChat: (
				payload: {
					remoteEntityId: string;
					conversationAri?: string;
				} | null,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			initializeTwilioConversation: (
				conversationId?: string | undefined,
				ocgTwilioClientModule?:
					| typeof import('../../extensions/twilio/ocg-twilio-client')
					| undefined,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			resolveConversationAction: (
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				args: ResolveConversationActionArgs<any>,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			sendMessage: (
				args_0: {
					prompt: DocNode;
					conversationId?: string;
					storeUserMessages: boolean;
					messageSources?: Partial<MessageSource>;
					sendEditorContext?: boolean;
					isClicked?: boolean;
					liveChatMessageOnly?: boolean;
					csmMediaAttachments?: CsmMediaAttachment[];
				} & SendMessageAgentParam,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			sendMessageConfirmedTools: (args_0: {
				conversationId?: string;
				agentId?: string;
				confirmed_tools: RovoLabsConfirmedTools;
				messageSources?: Partial<MessageSource>;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setChatConfiguration: (chatConfig: {
				chatMode: ChatMode;
				supportTeamFallbackMessage?: string | null;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setLiveChatSubscriptionId: (
				subscriptionId: string | null,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setLiveChatConversationAri: (
				conversationAri: string | null,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setCsmLiveChatSubscriptionId: (
				subscriptionId: string | null,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setSelectedConversation: (args_0: {
				conversationId: string;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			refetchConversations: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			restoreConversation: (args_0: {
				conversationId: string;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addTwilioAgentTyping: (
				params: TwilioAgentTypingParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			removeTwilioAgentTyping: (
				params: TwilioAgentTypingParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			clearTwilioAgentsTyping: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			setTwilioAgentJoined: (
				params: SetTwilioAgentJoinedParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			hydrateTwilioConversation: (
				params: HydrateTwilioConversationParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			updateTwilioUnreadMessagesCount: (
				params: UpdateTwilioUnreadMessagesCountParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			checkInitialUnreadMessages: (
				params: CheckInitialUnreadMessagesParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
		}
	>,
	void
> = createHook(chatStore, {
	selector: publicStateSelector,
});

/**
 * This is the preferred hook if component does not need any state, as this will never trigger re-render
 */
export const useChatStoreActions: HookActionsFunction<
	BoundActions<
		WidgetChatStorePrivateState,
		{
			abortRequests: (
				args_0?:
					| {
							conversationId?: string;
					  }
					| undefined,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addLiveChatMessage: (
				args_0: LiveChatMessage,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addTwilioLiveChatHumanMessage: (
				params: AddTwilioLiveChatMessageParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addTwilioLiveChatMessage: (
				params: AddTwilioLiveChatMessageParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			backfillLiveChatMessages: (
				params: BackfillLiveChatMessagesParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			cleanupTwilioTokenRefresh: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			clearFollowUpMessages: (args_0: {
				conversationId?: string;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			cleanupTwilioConversation: (
				conversationId: string,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			markTwilioConversationEnded: (
				conversationId: string,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			clearTwilioConversationsWithMetadata: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			closeLiveChat: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			createConversationOptimistically: (options: {
				name?: string;
				sourceId?: string;
				id?: string;
				timeCreated?: string;
				lastMessageTimestamp?: string;
				sourceAgentId?: string;
				status?: CHAT_STORE_STATUS;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			getChat: (args_0: {
				conversationId?: string;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			getCurrentConversation: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Conversation | undefined
			>;
			initConversationChannelsData: (args_0?: {
				autoSelectConversation?: boolean;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			initializeDirectLiveChat: (args_0: {
				helpCenterId: string;
				metadata?: Record<string, string>;
				/** Optional initial message (ADF DocNode) to render optimistically and send via Twilio. */
				initialMessage?: DocNode;
				/** Localized display name for the conversation (shown in chat history). */
				conversationName: string;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			initializeLiveChat: (
				payload: {
					remoteEntityId: string;
					conversationAri?: string;
				} | null,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			initializeTwilioConversation: (
				conversationId?: string | undefined,
				ocgTwilioClientModule?:
					| typeof import('../../extensions/twilio/ocg-twilio-client')
					| undefined,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			resolveConversationAction: (
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				args: ResolveConversationActionArgs<any>,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			sendMessage: (
				args_0: {
					prompt: DocNode;
					conversationId?: string;
					storeUserMessages: boolean;
					messageSources?: Partial<MessageSource>;
					sendEditorContext?: boolean;
					isClicked?: boolean;
					liveChatMessageOnly?: boolean;
					csmMediaAttachments?: CsmMediaAttachment[];
				} & SendMessageAgentParam,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			sendMessageConfirmedTools: (args_0: {
				conversationId?: string;
				agentId?: string;
				confirmed_tools: RovoLabsConfirmedTools;
				messageSources?: Partial<MessageSource>;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setChatConfiguration: (chatConfig: {
				chatMode: ChatMode;
				supportTeamFallbackMessage?: string | null;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setLiveChatSubscriptionId: (
				subscriptionId: string | null,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setLiveChatConversationAri: (
				conversationAri: string | null,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setCsmLiveChatSubscriptionId: (
				subscriptionId: string | null,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setSelectedConversation: (args_0: {
				conversationId: string;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			refetchConversations: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			restoreConversation: (args_0: {
				conversationId: string;
			}) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addTwilioAgentTyping: (
				params: TwilioAgentTypingParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			removeTwilioAgentTyping: (
				params: TwilioAgentTypingParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			clearTwilioAgentsTyping: () => Action<
				WidgetChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			setTwilioAgentJoined: (
				params: SetTwilioAgentJoinedParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			hydrateTwilioConversation: (
				params: HydrateTwilioConversationParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			updateTwilioUnreadMessagesCount: (
				params: UpdateTwilioUnreadMessagesCountParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			checkInitialUnreadMessages: (
				params: CheckInitialUnreadMessagesParams,
			) => Action<WidgetChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
		}
	>
> = createActionsHook(chatStore);
